import React, { useState, useEffect, useRef, useMemo } from 'react';
import documentService from '../../services/documentService';
import { supplierService } from '../../services/supplierService';
import { productService } from '../../services/productService';
import { serviceService } from '../../services/serviceService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { serviceOrderService } from '../../services/serviceOrderService';
import { getUnits } from '../../services/unitsService';
import { getProjects } from '../../services/projectsService';
import { getWarehouses } from '../../services/warehousesService';
import continuousServiceService from '../../services/continuousServiceService';
import SupplierSelector from '../PurchaseOrders/SupplierSelector';
import ProductSelector from '../PurchaseOrders/ProductSelector';
import UnitSelect from '../Common/UnitSelect';
import LineItemField from '../Common/LineItemField';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import PriceIgvHint from '../Common/PriceIgvHint';
import PricesIncludeIgvToggle from '../Common/PricesIncludeIgvToggle';
import UnitPriceTextInput from '../Common/UnitPriceTextInput';
import QuantityTextInput from '../Common/QuantityTextInput';
import { roundPrice } from '../../utils/priceIgvUtils';
import ServiceSelector from '../ServiceOrders/ServiceSelector';
import CreateSupplierModal from '../PurchaseOrders/CreateSupplierModal';
import Toast from '../Common/Toast';
import ConfirmModal from '../Common/ConfirmModal';
import FormLoadingSpinner from '../Common/FormLoadingSpinner';
import MachineryEventModal from '../Machinery/MachineryEventModal';
import { formatDateLocaleLima, formatDateToLima, getCurrentDateLima, getCurrentTimeLima } from '../../utils/dateUtils';
import { formatGuideSaveMessage } from '../../utils/kardexRecalculationHelpers';
import { formatBitacoraReading } from '../../utils/bitacoraFormat';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import ComprasItemsImportPreviewModal from '../Common/ComprasItemsImportPreviewModal';
import {
  downloadDocumentItemsTemplate,
  parseDocumentItemsExcel,
} from '../../utils/comprasItemsExcelImport';
import { getPurchaseOrdersWithDocumentMismatch } from '../../utils/purchaseOrderDocumentMismatch';
import MutateGate from '../Common/MutateGate';
import RequirementReferencePicker from '../Requirements/RequirementReferencePicker';
import SunatSearchResultModal from './SunatSearchResultModal';
import { fetchSunatDocument } from '../../services/sunatDocumentService';
import { getSunatDocTypeKey } from '../../utils/sunatDocumentTypes';
import { DOCUMENT_TYPE_OPTIONS, REFERENCE_DOCUMENT_TYPE_OPTIONS } from '../../utils/documentTypes';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { useComprasPurchaseSiteId } from '../../utils/comprasPurchaseSite';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import DocumentActivoModal from './DocumentActivoModal';
import { findFamilyPath } from '../../pages/Activos/Listado/ActivoForm';
import { applyConvertedLineDisplay } from '../../utils/unitSelectUtils';
import QuickProductLineBar from '../Common/QuickProductLineBar';
import ProductLineDefaultsBar from '../Common/ProductLineDefaultsBar';
import { emptyDocumentProductStagingItem } from '../../utils/quickProductLineFactories';
import { handlePreventEnterFormSubmit } from '../../utils/preventEnterFormSubmit';
import {
  applyProductLineDefaults,
  buildProductLineSettings,
  emptyStagingWithDefaults,
  mergeProductLineDefaults,
} from '../../utils/productLineDefaults';
import { DOC_ITEM_ROW, DIC } from '../../utils/documentItemRowLayout';
import {
  getActiveDocumentNumberSeries,
  peekDocumentNumberSeriesNext,
} from '../../services/documentNumberSeriesService';

// Redondeo a 4 decimales para subtotales y costos consistentes
const roundTo4 = (n) => Math.round(parseFloat(n) * 10000) / 10000;

const mergeUniqueById = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (item?.id != null) {
      map.set(Number(item.id), item);
    }
  });
  return Array.from(map.values());
};

// Singleton: catálogos livianos del formulario de documentos
const documentFormDataManager = {
  loadingPromise: null,
  hasLoaded: false,
  loading: false,
  cacheUserId: null,

  loadData(sessionUserId = null) {
    if (this.cacheUserId !== sessionUserId) {
      this.reset();
      this.cacheUserId = sessionUserId;
    }
    if (this.loading && this.loadingPromise) return this.loadingPromise;
    if (this.hasLoaded && this.loadingPromise) return this.loadingPromise;

    this.loading = true;
    this.loadingPromise = documentService
      .getFormData()
      .then((res) => {
        this.hasLoaded = true;
        this.loading = false;
        const d = res.data || {};
        return {
          suppliersRes: { success: true, data: d.suppliers || [] },
          productsRes: { success: true, data: d.products || [] },
          servicesRes: { success: true, data: d.services || [] },
          unitsRes: { success: true, data: d.units || [] },
          projectsRes: { success: true, data: d.projects || [] },
          warehousesRes: { success: true, data: d.warehouses || [] },
        };
      })
      .catch((error) => {
        this.loading = false;
        this.loadingPromise = null;
        throw error;
      });

    return this.loadingPromise;
  },

  reset() {
    this.loadingPromise = null;
    this.hasLoaded = false;
    this.loading = false;
    this.cacheUserId = null;
  },
};

const documentFormReferencesManager = {
  loadingPromise: null,
  hasLoaded: false,
  loading: false,
  lastParamsKey: '',

  loadData(force = false, params = {}) {
    const paramsKey = JSON.stringify(params);
    if (force || this.lastParamsKey !== paramsKey) {
      this.reset();
      this.lastParamsKey = paramsKey;
    }
    if (this.loading && this.loadingPromise) return this.loadingPromise;
    if (this.hasLoaded && this.loadingPromise) return this.loadingPromise;

    this.loading = true;
    this.loadingPromise = documentService
      .getFormReferences(params)
      .then((res) => {
        this.hasLoaded = true;
        this.loading = false;
        const d = res.data || {};
        return {
          poRes: { success: true, data: d.pending_purchase_orders || [] },
          soRes: { success: true, data: d.pending_service_orders || [] },
          egRes: { success: true, data: d.pending_entry_guides || [] },
          csRes: { success: true, data: d.closed_continuous_services || [] },
          reqRes: { success: true, data: d.pending_requirements || [] },
        };
      })
      .catch((error) => {
        this.loading = false;
        this.loadingPromise = null;
        throw error;
      });

    return this.loadingPromise;
  },

  reset() {
    this.loadingPromise = null;
    this.hasLoaded = false;
    this.loading = false;
    this.lastParamsKey = '';
  },
};

// Parsear número desde input (acepta "1,000", "1 000", "1000")
const parseNumberInput = (value) => {
  if (value === undefined || value === null || value === '') return NaN;
  const s = String(value).trim().replace(/\s/g, '').replace(/,/g, '');
  return parseFloat(s);
};

const DocumentForm = ({ isOpen, onClose, document = null, onSuccess, isPageMode = false, readOnly = false }) => {
  const [formData, setFormData] = useState({
    supplier_id: '',
    document_type: 'factura', // factura, boleta, recibo_por_honorarios, nota_credito, nota_debito, otro
    series: '',
    number: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'PEN',
    payment_condition_type: 'cash', // 'cash', 'days' o 'date'
    payment_condition_days: 30, // Días para pagar (si type es 'days')
    payment_condition_date: '', // Fecha específica (si type es 'date')
    reference_document_type: '', // Tipo de documento de referencia (para notas de crédito/débito)
    reference_document_date: '', // Fecha del documento de referencia
    reference_series: '', // Serie del documento de referencia
    reference_number: '', // Número del documento de referencia
    service_start_date: '', // Fecha de inicio del servicio
    service_end_date: '', // Fecha fin del servicio
    delivery_date: '', // Fecha de entrega de productos
    notes: '',
    igv_rate: 18, // Tasa IGV del documento: 18 o 10
    selected_purchase_orders: [], // IDs de órdenes de compra seleccionadas
    selected_service_orders: [], // IDs de órdenes de servicio seleccionadas
    selected_continuous_services: [], // IDs de servicios continuos seleccionados
    selected_entry_guides: [], // IDs de guías de entrada seleccionadas
    selected_requirements: [], // IDs de requerimientos anidados
    items: [], // Items del documento (pueden venir de órdenes o ser manuales)
  });

  const [suppliers, setSuppliers] = useState([]);
  const {
    products,
    getProduct: getCatalogProduct,
    mergeProducts,
    ensureByIds,
    searchProducts,
    resolveIdentifiers,
  } = useProductCatalog(null);
  const findProductById = (id, fallback = null) => getCatalogProduct(id, fallback);
  const productSelectorAsyncProps = {
    asyncSearch: true,
    knownProducts: products,
    onSearchProducts: searchProducts,
    onEnsureProduct: (id) => ensureByIds([id]),
  };
  const [services, setServices] = useState([]);
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [pendingPurchaseOrders, setPendingPurchaseOrders] = useState([]);
  const [pendingServiceOrders, setPendingServiceOrders] = useState([]);
  const [closedContinuousServices, setClosedContinuousServices] = useState([]);
  const [pendingEntryGuides, setPendingEntryGuides] = useState([]);
  const [pendingRequirements, setPendingRequirements] = useState([]);
  const [relatedPurchaseOrders, setRelatedPurchaseOrders] = useState([]);
  const [relatedEntryGuides, setRelatedEntryGuides] = useState([]);
  const [relatedContinuousServices, setRelatedContinuousServices] = useState([]);
  const [relatedRequirements, setRelatedRequirements] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [documentReady, setDocumentReady] = useState(() => !document?.id);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [codePreview, setCodePreview] = useState('');
  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [isOrderSelectorModalOpen, setIsOrderSelectorModalOpen] = useState(false);
  const [referencesLoading, setReferencesLoading] = useState(false);
  const [referencesLoaded, setReferencesLoaded] = useState(false);
  const [selectedSupplierInModal, setSelectedSupplierInModal] = useState('');
  const [isConfirmCreateModalOpen, setIsConfirmCreateModalOpen] = useState(false);
  const [isConfirmEditModalOpen, setIsConfirmEditModalOpen] = useState(false);
  const [updateEntryGuideWithDocument, setUpdateEntryGuideWithDocument] = useState(false);
  const [eventModalItemIndex, setEventModalItemIndex] = useState(null); // índice del ítem para abrir modal de registro de evento (bitácora)
  const [pendingMachineryEvents, setPendingMachineryEvents] = useState([]); // { itemIndex, ...eventData }[] para enviar con el documento y mostrar cuenta por ítem
  const [lastEventFormByItem, setLastEventFormByItem] = useState({}); // último formulario de evento por ítem para reabrir modal con datos
  const [loadedMachineryEventsByItem, setLoadedMachineryEventsByItem] = useState([]); // al editar: [{ itemIndex, displaySummary, eventId }] para mostrar debajo del producto
  const [entryGuideWarehouseId, setEntryGuideWarehouseId] = useState('');
  const [entryGuideTime, setEntryGuideTime] = useState('');
  const [createEntryGuideWithDocument, setCreateEntryGuideWithDocument] = useState(false);
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  const [amountOverride, setAmountOverride] = useState(null);
  const [amountForm, setAmountForm] = useState({ igv_amount: '' });
  const hasLoadedFormData = useRef(false);
  const hasLoadedDocument = useRef(false);
  const isLoadingRef = useRef(false);
  const initialEntryGuideIdsRef = useRef([]);
  const [entryGuidesSelectionTouched, setEntryGuidesSelectionTouched] = useState(false);
  const itemsImportFileRef = useRef(null);
  const [importingItems, setImportingItems] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [pricesIncludeIgv, setPricesIncludeIgv] = useState(false);
  const [closePoConfirm, setClosePoConfirm] = useState({ open: false, orderId: null, code: '' });
  const [closingPurchaseOrder, setClosingPurchaseOrder] = useState(false);
  const [sunatModalOpen, setSunatModalOpen] = useState(false);
  const [sunatData, setSunatData] = useState(null);
  const [sunatLoading, setSunatLoading] = useState(false);
  const { activosEnabled, productConversionsEnabled, quickProductLineEnabled, productsProjectEnabled, productsIgvEnabled } = useModuleSettings();
  const purchaseSiteId = useComprasPurchaseSiteId();
  const [defaultLineProjectId, setDefaultLineProjectId] = useState('');
  const [defaultLineHasIgv, setDefaultLineHasIgv] = useState(true);
  const productLineSettings = useMemo(
    () => buildProductLineSettings({
      productsProjectEnabled,
      productsIgvEnabled,
      defaultProjectId: defaultLineProjectId,
      defaultHasIgv: defaultLineHasIgv,
    }),
    [productsProjectEnabled, productsIgvEnabled, defaultLineProjectId, defaultLineHasIgv],
  );
  const showProductLineDefaults = productsProjectEnabled || productsIgvEnabled;
  const [stagingItem, setStagingItem] = useState(() => emptyDocumentProductStagingItem());
  const [stagingError, setStagingError] = useState('');
  const stagingProductSelectorRef = useRef(null);
  const [pendingActivos, setPendingActivos] = useState([]);
  const [autoNumberSeries, setAutoNumberSeries] = useState([]);
  const numberManuallyEditedRef = useRef(false);

  useEffect(() => {
    if (!showProductLineDefaults) return;
    setStagingItem((prev) => mergeProductLineDefaults(prev, productLineSettings));
  }, [productLineSettings, showProductLineDefaults]);

  useEffect(() => {
    if (document?.id || !(isOpen || isPageMode)) {
      return;
    }

    getActiveDocumentNumberSeries()
      .then((response) => {
        if (response.success) {
          setAutoNumberSeries(response.data || []);
        }
      })
      .catch(() => {
        setAutoNumberSeries([]);
      });
  }, [document?.id, isOpen, isPageMode]);

  const applyAutoNumberForSeries = async (seriesValue, documentType, options = {}) => {
    const { force = false } = options;
    if (document?.id) return;

    const series = (seriesValue || '').trim();
    if (!series) return;
    if (numberManuallyEditedRef.current && !force) return;

    try {
      const response = await peekDocumentNumberSeriesNext(series, documentType || null);
      if (!response.success || !response.data?.number) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        series: response.data.series || prev.series,
        number: response.data.number,
      }));
    } catch {
      // Serie no configurada: no hacer nada
    }
  };

  const [isActivoModalOpen, setIsActivoModalOpen] = useState(false);
  const [editingActivoIndex, setEditingActivoIndex] = useState(null);

  const purchaseOrderMismatches = useMemo(
    () =>
      getPurchaseOrdersWithDocumentMismatch(
        formData.items,
        formData.selected_purchase_orders,
        mergeUniqueById(pendingPurchaseOrders, relatedPurchaseOrders),
      ),
    [formData.items, formData.selected_purchase_orders, pendingPurchaseOrders, relatedPurchaseOrders],
  );

  const allPendingPurchaseOrders = useMemo(
    () => mergeUniqueById(pendingPurchaseOrders, relatedPurchaseOrders),
    [pendingPurchaseOrders, relatedPurchaseOrders],
  );
  const allPendingRequirements = useMemo(
    () => mergeUniqueById(pendingRequirements, relatedRequirements),
    [pendingRequirements, relatedRequirements],
  );
  const allPendingEntryGuides = useMemo(
    () => mergeUniqueById(pendingEntryGuides, relatedEntryGuides),
    [pendingEntryGuides, relatedEntryGuides],
  );

  const entryGuideWarehouses = useMemo(() => {
    return filterWarehousesForUser(warehouses, authService.getCurrentUser());
  }, [warehouses]);

  const shouldOfferEntryGuideOnCreate = () => {
    const hasProductsForWarehouse = formData.items.some(
      (item) => item.item_type === 'product' && !item.is_direct_consumption,
    );
    const hasOnlyServices = formData.items.length > 0
      && formData.items.every((item) => item.item_type === 'service');
    const hasEntryGuides = (formData.selected_entry_guides || []).length > 0;
    return hasProductsForWarehouse && !hasOnlyServices && !hasEntryGuides;
  };

  const resolveEditLinkedEntryGuideIds = () => {
    const fromFormGuides = sanitizeEntryGuideIds(formData.selected_entry_guides);
    const fromItems = sanitizeEntryGuideIds(
      formData.items
        .map((item) => item.entry_guide_id)
        .filter(Boolean),
    );
    const fromRelated = sanitizeEntryGuideIds(
      (relatedEntryGuides || []).map((guide) => guide.id),
    );

    return sanitizeEntryGuideIds([...fromFormGuides, ...fromItems, ...fromRelated]);
  };

  const resolveEditLinkedEntryGuides = () => {
    const ids = new Set(resolveEditLinkedEntryGuideIds());

    return mergeUniqueById(relatedEntryGuides || [], pendingEntryGuides || [])
      .filter((guide) => ids.has(Number(guide.id)));
  };

  const resolveWarehouseFulfillmentGuidesForEdit = () => (
    resolveEditLinkedEntryGuides().filter(
      (guide) => guide.is_warehouse_fulfillment || Number(guide.document_id) === Number(document?.id),
    )
  );

  const hasLinkedEntryGuidesOnEdit = () => (
    resolveEditLinkedEntryGuideIds().length > 0
    || formData.items.some((item) => item.entry_guide_item_id)
  );

  const shouldOfferEntryGuideOnUpdate = () => (
    resolveWarehouseFulfillmentGuidesForEdit().length > 0
  );

  const hasSharedEntryGuideRisk = () => {
    const linkedGuides = resolveEditLinkedEntryGuides();

    if (linkedGuides.some((guide) => (guide.linked_invoice_documents || []).length > 1)) {
      return true;
    }

    return linkedGuides.some(
      (guide) => !guide.is_warehouse_fulfillment && Number(guide.document_id) !== Number(document?.id),
    );
  };

  const getSharedEntryGuideWarningDetails = () => {
    const linkedGuides = resolveEditLinkedEntryGuides();
    const multiDocGuides = linkedGuides.filter(
      (guide) => (guide.linked_invoice_documents || []).length > 1,
    );

    if (multiDocGuides.length > 0) {
      return multiDocGuides
        .map((guide) => {
          const docs = (guide.linked_invoice_documents || [])
            .map((doc) => doc.reference_label || doc.code)
            .join(', ');
          return `${guide.code}: ${docs}`;
        })
        .join(' · ');
    }

    return linkedGuides.map((guide) => guide.code).filter(Boolean).join(', ');
  };

  const isInitialLoading = !catalogLoaded || !documentReady;

  // Cargar datos del formulario
  useEffect(() => {
    // Verificar condiciones básicas
    if (!(isOpen || isPageMode)) {
      return;
    }
    
    if (hasLoadedFormData.current) {
      return;
    }
    
    // Marcar esta instancia como cargada ANTES de llamar al DataManager
    // Esto previene múltiples llamadas desde la misma instancia
    hasLoadedFormData.current = true;
    setCatalogLoaded(false);
    if (document?.id) {
      setDocumentReady(false);
    }
    
    // Llamar al DataManager - este método es SÍNCRONO y retorna una promesa
    // Si ya está cargando, retorna la misma promesa sin hacer nuevas llamadas
    const dataPromise = documentFormDataManager.loadData(authService.getCurrentUser()?.id ?? null);
    
    // Esperar la promesa (puede ser la misma si ya estaba cargando)
    dataPromise.then(({ suppliersRes, productsRes, servicesRes, unitsRes, projectsRes, warehousesRes }) => {
      if (suppliersRes.success) {
        setSuppliers(suppliersRes.data);
      }
      if (productsRes && productsRes.success) {
        // Catálogo liviano: productos se buscan bajo demanda.
      }
      if (servicesRes && servicesRes.success) {
        setServices(servicesRes.data || []);
      }
      if (unitsRes && unitsRes.success) {
        setUnits(unitsRes.data || []);
      }
      if (projectsRes && projectsRes.success) {
        setProjects(projectsRes.data || []);
      }
      if (warehousesRes && warehousesRes.success) {
        const user = authService.getCurrentUser();
        setWarehouses(filterWarehousesForUser(warehousesRes.data || [], user));
      }
    }).catch((error) => {
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    }).finally(() => {
      setCatalogLoaded(true);
      if (!document?.id) {
        setDocumentReady(true);
      }
    });
  }, [isOpen, isPageMode, document?.id]);

  // Al cerrar el modal, resetear caché para que al reabrir se recarguen catálogos y referencias
  useEffect(() => {
    if (!isOpen && !isPageMode) {
      documentFormDataManager.reset();
      documentFormReferencesManager.reset();
      hasLoadedFormData.current = false;
      hasLoadedDocument.current = false;
      setCatalogLoaded(false);
      setReferencesLoaded(false);
      setReferencesLoading(false);
      setDocumentReady(!document?.id);
    }
  }, [isOpen, isPageMode, document?.id]);

  const applyFormReferences = (refs, related = {}) => {
    const {
      relatedPurchaseOrders: relatedPo = [],
      relatedEntryGuides: relatedEg = [],
      relatedContinuousServices: relatedCs = [],
      relatedRequirements: relatedReq = [],
    } = related;

    if (refs.poRes?.success) {
      setPendingPurchaseOrders(mergeUniqueById(refs.poRes.data || [], relatedPo));
    }
    if (refs.soRes?.success) {
      setPendingServiceOrders(refs.soRes.data || []);
    }
    if (refs.csRes?.success) {
      setClosedContinuousServices(mergeUniqueById(refs.csRes.data || [], relatedCs));
    }
    if (refs.egRes?.success) {
      setPendingEntryGuides(mergeUniqueById(refs.egRes.data || [], relatedEg));
    }
    if (refs.reqRes?.success) {
      setPendingRequirements(mergeUniqueById(refs.reqRes.data || [], relatedReq));
    }
    setReferencesLoaded(true);
  };

  const openOrderSelectorModal = () => {
    setIsOrderSelectorModalOpen(true);
    if (referencesLoaded || referencesLoading) return;

    setReferencesLoading(true);
    const refParams = purchaseSiteId ? { purchase_site_id: purchaseSiteId } : {};
    documentFormReferencesManager
      .loadData(false, refParams)
      .then((refs) => {
        applyFormReferences(refs, {
          relatedPurchaseOrders,
          relatedEntryGuides,
          relatedContinuousServices,
          relatedRequirements,
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'Error al cargar las referencias pendientes',
          type: 'error',
        });
        setIsOrderSelectorModalOpen(false);
      })
      .finally(() => {
        setReferencesLoading(false);
      });
  };

  useEffect(() => {
    const ids = (formData.items || [])
      .filter((item) => item.item_type === 'product')
      .map((item) => item.product_id)
      .filter(Boolean);
    if (ids.length > 0) ensureByIds(ids).catch(() => {});
  }, [formData.items, ensureByIds]);

  // Generar preview de código
  useEffect(() => {
    if (!document) {
      setCodePreview('DOC-XXXX');
    }
  }, [document]);

  // Cargar documento si se pasa un ID (modo edición)
  useEffect(() => {
    if (!document || typeof document !== 'object' || !document.id) {
      setDocumentReady(true);
      return;
    }
    if (!catalogLoaded) return;
    if (hasLoadedDocument.current === document.id) {
      setDocumentReady(true);
      return;
    }
    hasLoadedDocument.current = document.id;
    setDocumentReady(false);
    loadDocumentDataFromObject(document, products || [], services || []);
    setDocumentReady(true);
  }, [document, products, services, catalogLoaded]);

  // Limpiar errores de fechas cuando se completen
  useEffect(() => {
    const hasDeliveryDate = formData.delivery_date && formData.delivery_date.trim() !== '';
    const hasServiceDates = formData.service_start_date && formData.service_start_date.trim() !== '' && 
                           formData.service_end_date && formData.service_end_date.trim() !== '';
    
    if (hasDeliveryDate || hasServiceDates) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (hasDeliveryDate || hasServiceDates) {
          delete newErrors.delivery_date;
          delete newErrors.service_start_date;
        }
        return newErrors;
      });
    }
  }, [formData.delivery_date, formData.service_start_date, formData.service_end_date]);


  const getItemEntryGuideLink = (item) => {
    const guideItem = item.entry_guide_item || item.entryGuideItem || null;
    return {
      entry_guide_item_id: item.entry_guide_item_id || guideItem?.id || null,
      entry_guide_id: Number(
        guideItem?.entry_guide_id
        ?? guideItem?.entry_guide?.id
        ?? item.entry_guide_id
        ?? null,
      ),
    };
  };

  const sanitizeEntryGuideIds = (ids) => (
    [...new Set(
      (ids || [])
        .map((id) => parseInt(id, 10))
        .filter((id) => Number.isFinite(id) && id > 0),
    )]
  );

  const resolveLinkedEntryGuideIds = (documentData) => (
    sanitizeEntryGuideIds((documentData.entry_guides || []).map((guide) => guide.id))
  );

  const resolveEntryGuideIdsForSubmit = (selectedIds) => (
    sanitizeEntryGuideIds(selectedIds)
  );

  const loadDocumentDataFromObject = (documentData, productsList = [], servicesList = []) => {
    const linkedEntryGuideIds = resolveLinkedEntryGuideIds(documentData);
    const linkedEntryGuides = documentData.entry_guides || [];
    initialEntryGuideIdsRef.current = linkedEntryGuideIds;
    setEntryGuidesSelectionTouched(false);

    // Guardar órdenes de compra, guías y servicios continuos relacionadas
    setRelatedPurchaseOrders(documentData.purchase_orders || []);
    setRelatedEntryGuides(linkedEntryGuides);
    setRelatedContinuousServices(documentData.continuous_services || []);
    setRelatedRequirements(documentData.requirements || []);
    setPendingEntryGuides((prev) => {
      const ids = new Set(prev.map((guide) => Number(guide.id)));
      const merged = [...prev];
      linkedEntryGuides.forEach((guide) => {
        if (!ids.has(Number(guide.id))) {
          merged.push(guide);
          ids.add(Number(guide.id));
        }
      });
      return merged;
    });
    
    setFormData({
      supplier_id: documentData.supplier_id || '',
      document_type: documentData.document_type || 'factura',
      series: documentData.series || '',
      number: documentData.number || '',
      issue_date: documentData.issue_date ? new Date(documentData.issue_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      due_date: documentData.due_date ? new Date(documentData.due_date).toISOString().split('T')[0] : '',
      currency: documentData.currency || 'PEN',
      payment_condition_type: documentData.payment_condition_type || 'cash',
      payment_condition_days: documentData.payment_condition_days || 30,
      payment_condition_date: documentData.payment_condition_date ? new Date(documentData.payment_condition_date).toISOString().split('T')[0] : '',
      reference_document_type: documentData.reference_document_type || '',
      reference_document_date: documentData.reference_document_date ? new Date(documentData.reference_document_date).toISOString().split('T')[0] : '',
      reference_series: documentData.reference_series || '',
      reference_number: documentData.reference_number || '',
      service_start_date: documentData.service_start_date ? new Date(documentData.service_start_date).toISOString().split('T')[0] : '',
      service_end_date: documentData.service_end_date ? new Date(documentData.service_end_date).toISOString().split('T')[0] : '',
      delivery_date: documentData.delivery_date ? new Date(documentData.delivery_date).toISOString().split('T')[0] : '',
      notes: documentData.notes || '',
      igv_rate: documentData.igv_rate ?? 18,
      selected_purchase_orders: documentData.purchase_orders?.map(po => po.id) || [],
      selected_service_orders: documentData.service_orders?.map(so => so.id) || [],
      selected_continuous_services: documentData.continuous_services?.map(cs => cs.id) || [],
      selected_requirements: documentData.requirements?.map((r) => r.id) || [],
      selected_entry_guides: linkedEntryGuideIds,
      items: documentData.items?.map(item => {
        const progressIds = (item.continuous_service_progress_ids && item.continuous_service_progress_ids.length)
          ? item.continuous_service_progress_ids
          : (item.continuous_service_progress_id ? [item.continuous_service_progress_id] : []);
        const guideLink = getItemEntryGuideLink(item);
        return {
          id: item.id,
          item_type: item.item_type || 'product',
          product_id: item.product_id || '',
          service_id: item.service_id || '',
          description: item.description || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit_of_measure: item.unit_of_measure || item.product?.unit_of_measure || '',
          project_id: item.project_id || '',
          has_igv: item.has_igv !== undefined ? item.has_igv : true,
          subtotal: item.subtotal,
          tax_rate: item.tax_rate ?? (documentData.igv_rate ?? 18),
          tax_amount: item.tax_amount || 0,
          total: item.total,
          is_direct_consumption: item.is_direct_consumption ?? false,
          continuous_service_progress_id: item.continuous_service_progress_id || (progressIds[0] || null),
          continuous_service_progress_ids: progressIds.length ? progressIds : null,
          requirement_item_id: item.requirement_item_id || null,
          requirement_id: item.requirement_item?.requirement_id || item.requirement_id || null,
          purchase_order_item_id: item.purchase_order_item_id || null,
          purchase_order_id: item.purchase_order_item?.purchase_order_id || null,
          entry_guide_item_id: guideLink.entry_guide_item_id,
          entry_guide_id: Number.isFinite(guideLink.entry_guide_id) ? guideLink.entry_guide_id : null,
        };
      }) || [],
    });

    if (documentData.code) {
      setCodePreview(documentData.code);
    }
    // Eventos de bitácora del documento: ítems con seguimiento (producto consumo directo con flag o servicio con machinery_register_enabled)
    const machineryEvents = documentData.machinery_events || [];
    if (machineryEvents.length > 0 && documentData.items?.length) {
      const itemIndicesWithEvent = documentData.items
        .map((it, idx) => idx)
        .filter((i) => {
          const it = documentData.items[i];
          if (it.item_type === 'product') return it.is_direct_consumption && (productsList.find(p => p.id === parseInt(it.product_id))?.machinery_register_enabled);
          if (it.item_type === 'service') return servicesList.find(s => s.id === parseInt(it.service_id))?.machinery_register_enabled;
          return false;
        });
      const loaded = [];
      const formByItem = {};
      const itemIndexByDocumentItemId = new Map(
        (documentData.items || [])
          .map((it, idx) => [it?.id != null ? Number(it.id) : null, idx])
          .filter(([id]) => Number.isFinite(id))
      );
      machineryEvents.forEach((ev, pos) => {
        const mappedByDocumentItem = ev?.document_item_id != null
          ? itemIndexByDocumentItemId.get(Number(ev.document_item_id))
          : undefined;
        const itemIndex = (mappedByDocumentItem ?? itemIndicesWithEvent[pos] ?? itemIndicesWithEvent[itemIndicesWithEvent.length - 1] ?? 0);
        const parts = [];
        if (ev.vehicle_id && ev.vehicle) parts.push(`Vehículo: ${ev.vehicle.code || ''} - ${ev.vehicle.name || ''}`);
        else if (ev.machine_id && ev.machine) parts.push(`Máquina: ${ev.machine.code || ''} - ${ev.machine.name || ''}`);
        if (ev.event_time) parts.push(String(ev.event_time).substring(0, 5));
        if (ev.hours != null) parts.push(`${formatBitacoraReading(ev.hours)} h`);
        if (ev.kilometers != null) parts.push(`${formatBitacoraReading(ev.kilometers)} km`);
        if (ev.conductor?.name) parts.push(ev.conductor.name);
        if (ev.event_type?.name) parts.push(ev.event_type.name);
        if (ev.project?.name) parts.push(ev.project.name);
        const displaySummary = parts.length ? parts.join(' · ') : 'Evento';
        loaded.push({ itemIndex, displaySummary, eventId: ev.id });
        formByItem[itemIndex] = {
          event_date: ev.event_date ? new Date(ev.event_date).toISOString().split('T')[0] : '',
          event_time: ev.event_time ? String(ev.event_time).substring(0, 5) : '',
          record_type: ev.vehicle_id ? 'vehicle' : 'machine',
          machine_id: ev.machine_id != null ? String(ev.machine_id) : '',
          vehicle_id: ev.vehicle_id != null ? String(ev.vehicle_id) : '',
          hours: ev.hours != null ? ev.hours : '',
          kilometers: ev.kilometers != null ? ev.kilometers : '',
          operator: ev.conductor?.name ?? '',
          event_type: ev.event_type?.name ?? '',
          project_id: ev.project_id != null ? String(ev.project_id) : '',
          notes: ev.notes ?? ''
        };
      });
      setLoadedMachineryEventsByItem(loaded);
      setLastEventFormByItem((prev) => ({ ...prev, ...formByItem }));
    } else {
      setLoadedMachineryEventsByItem([]);
    }

    setPendingActivos(
      (documentData.activos || []).map((a) => ({
        id: a.id,
        code: a.code,
        name: a.name,
        description: a.description || '',
        activo_family_id: a.activo_family_id,
        area_id: a.area_id,
        brand: a.brand || '',
        model: a.model || '',
        serial_number: a.serial_number || '',
        status: a.status || 'pending_start',
        notes: a.notes || '',
        is_active: a.is_active !== undefined ? a.is_active : true,
        acquisition_quantity: a.acquisition_quantity ?? 1,
        acquisition_unit_price: a.acquisition_unit_price ?? null,
        family: a.family,
        area: a.area,
      }))
    );

    // Mostrar los totales guardados en BD (no recalcular al entrar a editar)
    const base = parseFloat(documentData.base_imponible);
    const igv = parseFloat(documentData.igv_amount);
    const total = parseFloat(documentData.total_amount);
    const noGrav = parseFloat(documentData.no_gravados ?? 0);
    if (Number.isFinite(base) && Number.isFinite(igv) && Number.isFinite(total)) {
      setAmountOverride({
        baseImponible: base,
        igv,
        noGravados: Number.isFinite(noGrav) ? noGrav : 0,
        total,
      });
    } else {
      setAmountOverride(null);
    }

    mergeProducts(
      (documentData.items || [])
        .filter((item) => item.item_type === 'product')
        .map((item) => item.product)
        .filter(Boolean),
    );
    mergeProducts(productsList || []);
  };

  const handleSunatSearch = async () => {
    const { supplier_id, document_type, series, number, issue_date } = formData;

    if (!supplier_id) {
      setToast({ isOpen: true, message: 'Selecciona un proveedor antes de buscar en SUNAT', type: 'warning' });
      return;
    }
    if (!series || !number) {
      setToast({ isOpen: true, message: 'Ingresa la serie y número del documento', type: 'warning' });
      return;
    }
    if (!issue_date) {
      setToast({ isOpen: true, message: 'Ingresa la fecha de emisión', type: 'warning' });
      return;
    }

    setSunatLoading(true);
    setSunatData(null);

    try {
      const res = await fetchSunatDocument({
        supplier_id,
        document_type,
        series,
        number,
        issue_date,
      });

      if (res.success && res.data) {
        setSunatData(res.data);
        setSunatModalOpen(true);
      } else {
        setSunatData(null);
        setToast({
          isOpen: true,
          message: res.message || 'No se encontró el documento en SUNAT',
          type: 'error',
        });
      }
    } catch (err) {
      setSunatData(null);
      setToast({
        isOpen: true,
        message: err?.response?.data?.message || 'Error de conexión al consultar SUNAT',
        type: 'error',
      });
    } finally {
      setSunatLoading(false);
    }
  };

  const handleSunatApply = (data) => {
    setFormData((prev) => {
      const igvRate = prev.igv_rate || 18;
      const newItems = (data.products || [])
        .filter((p) => p.product_id != null)
        .map((p) => {
        const qty = p.quantity || 0;
        const unitPrice = p.unit_price || 0;
        const subtotal = roundTo4(qty * unitPrice);
        const taxAmount = roundTo4(subtotal * (igvRate / 100));
        const unit = p.unit_id ? units.find((u) => u.id === p.unit_id) : null;
        return {
          item_type: 'product',
          product_id: p.product_id || null,
          service_id: null,
          unit_of_measure: (unit && unit.code) || p.unit_code || '',
          project_id: null,
          description: p.description || '',
          quantity: qty,
          unit_price: unitPrice,
          has_igv: true,
          subtotal,
          tax_rate: igvRate,
          tax_amount: taxAmount,
          total: roundTo4(subtotal + taxAmount),
          is_direct_consumption: false,
        };
      });

      return {
        ...prev,
        series: data.series || prev.series,
        number: data.number || prev.number,
        issue_date: data.issue_date || prev.issue_date,
        document_type: getSunatDocTypeKey(data.document_type) || prev.document_type,
        currency: data.currency || prev.currency,
        notes: data.notes
          ? prev.notes
            ? prev.notes + '\n' + data.notes
            : data.notes
          : prev.notes,
        items: [...prev.items, ...newItems],
      };
    });

    setAmountOverride({
      baseImponible: data.base_imponible,
      igv: data.igv,
      noGravados: 0,
      total: data.total,
    });

    setSunatModalOpen(false);
  };

  const handleSupplierCreated = async (newSupplier) => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true, entity_provider: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al recargar proveedores:', error);
      setSuppliers(prev => [...prev, newSupplier]);
    }
    
    setFormData(prev => ({
      ...prev,
      supplier_id: newSupplier.id.toString(),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'igv_rate') setAmountOverride(null);
    if (name === 'number' && !document?.id) {
      numberManuallyEditedRef.current = true;
    }
    if (name === 'series' && !document?.id) {
      numberManuallyEditedRef.current = false;
    }
    setFormData(prev => {
      const next = { ...prev, [name]: name === 'igv_rate' ? (parseFloat(value) || 18) : value };
      // Al cambiar la tasa IGV, recalcular tax_amount y total de todos los ítems con IGV
      if (name === 'igv_rate') {
        const rate = next.igv_rate ?? 18;
        next.items = prev.items.map(item => {
          const hasIgv = item.has_igv !== undefined ? item.has_igv : true;
          const subtotal = parseFloat(item.subtotal) || 0;
          const taxRate = hasIgv ? rate : 0;
          const taxAmount = roundTo4(subtotal * (taxRate / 100));
          const total = roundTo4(subtotal + taxAmount);
          return { ...item, tax_rate: taxRate, tax_amount: taxAmount, total };
        });
      }
      return next;
    });

    if (!document?.id && (name === 'series' || name === 'document_type')) {
      const series = name === 'series' ? value : formData.series;
      const documentType = name === 'document_type' ? value : formData.document_type;
      applyAutoNumberForSeries(series, documentType, { force: name === 'document_type' });
    }
    
    // Limpiar errores del campo actual
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
    
    // Si se ingresa una fecha de entrega o de servicio, limpiar los errores relacionados
    if (name === 'delivery_date' && value && value.trim() !== '') {
      setErrors(prev => ({
        ...prev,
        delivery_date: null,
        service_start_date: null,
      }));
    }
    
  };

  // Seleccionar órdenes de compra
  const handleSelectPurchaseOrder = (orderId, checked) => {
    setAmountOverride(null);
    setFormData(prev => {
      const newSelected = checked
        ? [...prev.selected_purchase_orders, orderId]
        : prev.selected_purchase_orders.filter(id => id !== orderId);
      
      // Agregar items de las órdenes seleccionadas
      const newItems = [...prev.items];
      let updatedFormData = { ...prev };
      
      if (checked) {
        const order = allPendingPurchaseOrders.find((po) => po.id === orderId);
        if (order) {
          // Si no hay proveedor seleccionado, usar el de la orden
          if (!prev.supplier_id && order.supplier_id) {
            updatedFormData.supplier_id = order.supplier_id.toString();
          }
          
          // Si no hay condición de pago configurada, usar la de la orden
          if (order.payment_condition_type && !prev.payment_condition_type) {
            updatedFormData.payment_condition_type = order.payment_condition_type;
            updatedFormData.payment_condition_days = order.payment_condition_days || 30;
            updatedFormData.payment_condition_date = order.payment_condition_date || '';
          }
          
          if (order.items) {
            order.items.forEach(item => {
              const qty = item.pending_quantity !== undefined && item.pending_quantity !== null
                ? parseFloat(item.pending_quantity)
                : parseFloat(item.quantity) || 0;
              if (qty <= 0) return;
              if (!newItems.find(i => i.purchase_order_item_id === item.id)) {
                const unitPrice = parseFloat(item.unit_price) || 0;
                const product =
                  item.product || findProductById(item.product_id, item.product);
                const useConverted = order.use_converted_quantity_for_subtotal !== false;
                const rawUnit = item.unit_of_measure || item.product?.unit_of_measure || '';
                let lineItem = applySubtotalModeDisplay({
                  purchase_order_item_id: item.id,
                  purchase_order_id: orderId,
                  item_type: 'product',
                  product_id: item.product_id,
                  product,
                  unit_of_measure: rawUnit,
                  project_id: item.project_id || '',
                  description: item.product?.name || '',
                  quantity: qty,
                  unit_price: unitPrice,
                  has_igv: item.has_igv !== undefined ? item.has_igv : true,
                  _source_quantity: qty,
                  _source_unit_of_measure: rawUnit,
                }, { useConverted });
                const subtotal = resolveItemLineSubtotal(lineItem);
                const docRate = updatedFormData.igv_rate ?? 18;
                const taxAmount = item.has_igv ? roundTo4(subtotal * (docRate / 100)) : 0;
                newItems.push({
                  ...lineItem,
                  subtotal,
                  tax_rate: item.has_igv ? docRate : 0,
                  tax_amount: taxAmount,
                  total: roundTo4(subtotal + taxAmount),
                });
              }
            });
          }
        }
      } else {
        // Remover items de la orden deseleccionada
        const order = allPendingPurchaseOrders.find((po) => po.id === orderId);
        if (order && order.items) {
          order.items.forEach(item => {
            const index = newItems.findIndex(i => i.purchase_order_item_id === item.id);
            if (index !== -1) {
              newItems.splice(index, 1);
            }
          });
        }
      }
      
      return {
        ...updatedFormData,
        selected_purchase_orders: newSelected,
        items: newItems,
      };
    });
  };

  // Seleccionar requerimientos (cantidades pendientes, sin proveedor)
  const handleSelectRequirement = (reqId, checked) => {
    setAmountOverride(null);
    setFormData((prev) => {
      const newSelected = checked
        ? [...prev.selected_requirements, reqId]
        : prev.selected_requirements.filter((id) => id !== reqId);

      const newItems = [...prev.items];
      let updatedFormData = { ...prev };
      const docRate = updatedFormData.igv_rate ?? 18;

      if (checked) {
        const req = allPendingRequirements.find((r) => r.id === reqId);
        if (req?.items) {
          req.items.forEach((ri) => {
            const qty =
              ri.pending_quantity !== undefined && ri.pending_quantity !== null
                ? parseFloat(ri.pending_quantity)
                : parseFloat(ri.quantity) || 0;
            if (qty <= 0) return;
            if (!newItems.find((i) => i.requirement_item_id === ri.id)) {
              const unitPrice = 0;
              const subtotal = roundTo4(qty * unitPrice);
              const taxAmount = 0;
              newItems.push({
                requirement_item_id: ri.id,
                requirement_id: req.id,
                item_type: 'product',
                product_id: ri.product_id,
                unit_of_measure: ri.unit_of_measure || ri.product?.unit_of_measure || '',
                project_id: ri.project_id || '',
                description: ri.product?.name || '',
                quantity: qty,
                unit_price: unitPrice,
                has_igv: true,
                subtotal,
                tax_rate: docRate,
                tax_amount: taxAmount,
                total: roundTo4(subtotal + taxAmount),
              });
            }
          });
        }
      } else {
        const req = allPendingRequirements.find((r) => r.id === reqId);
        if (req?.items) {
          const riIds = new Set(req.items.map((i) => i.id));
          for (let i = newItems.length - 1; i >= 0; i--) {
            if (newItems[i].requirement_item_id && riIds.has(newItems[i].requirement_item_id)) {
              newItems.splice(i, 1);
            }
          }
        }
      }

      return {
        ...updatedFormData,
        selected_requirements: newSelected,
        items: newItems,
      };
    });
  };

  const applySelectedRequirementsFromModal = () => {
    setIsOrderSelectorModalOpen(false);
    setToast({
      isOpen: true,
      message: 'Requerimientos aplicados. Complete precios en los ítems si corresponde.',
      type: 'success',
    });
  };

  // Seleccionar órdenes de servicio
  const handleSelectServiceOrder = (orderId, checked) => {
    setAmountOverride(null);
    setFormData(prev => {
      const newSelected = checked
        ? [...prev.selected_service_orders, orderId]
        : prev.selected_service_orders.filter(id => id !== orderId);
      
      // Agregar items de las órdenes seleccionadas
      const newItems = [...prev.items];
      let updatedFormData = { ...prev };
      
      if (checked) {
        const order = pendingServiceOrders.find(so => so.id === orderId);
        if (order) {
          // Si no hay proveedor seleccionado, usar el de la orden
          if (!prev.supplier_id && order.supplier_id) {
            updatedFormData.supplier_id = order.supplier_id.toString();
          }
          
          // Si no hay condición de pago configurada, usar la de la orden
          if (order.payment_condition_type && !prev.payment_condition_type) {
            updatedFormData.payment_condition_type = order.payment_condition_type;
            updatedFormData.payment_condition_days = order.payment_condition_days || 30;
            updatedFormData.payment_condition_date = order.payment_condition_date || '';
          }
          
          if (order.items) {
            order.items.forEach(item => {
              const qty = item.pending_quantity !== undefined && item.pending_quantity !== null
                ? parseFloat(item.pending_quantity)
                : parseFloat(item.quantity) || 0;
              if (qty <= 0) return;
              if (!newItems.find(i => i.service_order_item_id === item.id)) {
                const unitPrice = parseFloat(item.unit_price) || 0;
                const subtotal = roundTo4(qty * unitPrice);
                const docRate = updatedFormData.igv_rate ?? 18;
                const taxAmount = item.has_igv ? roundTo4(subtotal * (docRate / 100)) : 0;
                // Obtener la descripción: primero del item, luego del servicio relacionado, o buscar en la lista de servicios cargados
                let description = item.description || '';
                if (!description && item.service?.name) {
                  description = item.service.name;
                }
                if (!description && item.service_id) {
                  const service = services.find(s => s.id === parseInt(item.service_id));
                  if (service && service.name) {
                    description = service.name;
                  }
                }
                if (!description) {
                  description = 'Servicio sin descripción';
                }
                newItems.push({
                  service_order_item_id: item.id,
                  service_order_id: orderId,
                  item_type: 'service',
                  service_id: item.service_id,
                  project_id: item.project_id || '',
                  description: description || 'Servicio sin descripción',
                  quantity: qty,
                  unit_price: unitPrice,
                  has_igv: item.has_igv !== undefined ? item.has_igv : true,
                  subtotal,
                  tax_rate: item.has_igv ? docRate : 0,
                  tax_amount: taxAmount,
                  total: roundTo4(subtotal + taxAmount),
                });
              }
            });
          }
        }
      } else {
        // Remover items de la orden deseleccionada
        const order = pendingServiceOrders.find(so => so.id === orderId);
        if (order && order.items) {
          order.items.forEach(item => {
            const index = newItems.findIndex(i => i.service_order_item_id === item.id);
            if (index !== -1) {
              newItems.splice(index, 1);
            }
          });
        }
      }
      
      return {
        ...updatedFormData,
        selected_service_orders: newSelected,
        items: newItems,
      };
    });
  };

  // Seleccionar servicios continuos
  const handleSelectContinuousService = (serviceId, checked) => {
    setAmountOverride(null); // Para que Base Imponible y Total se recalculen desde los ítems
    setFormData(prev => {
      const newSelected = checked
        ? [...(prev.selected_continuous_services || []), serviceId]
        : (prev.selected_continuous_services || []).filter(id => id !== serviceId);
      
      // Agregar items de los servicios continuos seleccionados
      const newItems = [...prev.items];
      let updatedFormData = { ...prev };
      
      if (checked) {
        const service = closedContinuousServices.find(cs => cs.id === serviceId);
        console.log('Servicio continuo seleccionado:', service);
        if (service) {
          // Si no hay proveedor seleccionado, usar el del servicio
          if (!prev.supplier_id && service.supplier_id) {
            updatedFormData.supplier_id = service.supplier_id.toString();
          }
          
          // Establecer fechas de inicio y fin del servicio continuo
          if (service.start_date && !prev.service_start_date) {
            updatedFormData.service_start_date = service.start_date.split('T')[0]; // Extraer solo la fecha (YYYY-MM-DD)
          }
          if (service.end_date && !prev.service_end_date) {
            updatedFormData.service_end_date = service.end_date.split('T')[0]; // Extraer solo la fecha (YYYY-MM-DD)
          }
          
          // Agregar items desde los registros de avance del servicio continuo
          // Agrupar por SKU de servicio y sumar cantidades, pero mantener proyectos separados
          console.log('Progress del servicio:', service.progress);
          if (service.progress && service.progress.length > 0) {
            // Agrupar por SKU del servicio (service.sku), project_id y has_igv
            // Si tienen el mismo SKU pero proyectos distintos, crear items separados
            const groupedBySku = {};
            
            service.progress.forEach(progressItem => {
              const serviceSku = progressItem.service?.sku || '';
              const serviceId = progressItem.service_id || '';
              const projectId = progressItem.project_id || 'no-project';
              
              // Crear una clave única basada en SKU, project_id y has_igv
              // Si tienen el mismo SKU pero proyectos distintos, se crean items separados
              const groupKey = `${serviceSku || serviceId}-${projectId}-${progressItem.has_igv !== false ? 'igv' : 'no-igv'}`;
              
              if (!groupedBySku[groupKey]) {
                groupedBySku[groupKey] = {
                  service_id: serviceId,
                  service: progressItem.service,
                  project_id: progressItem.project_id || '',
                  project: progressItem.project,
                  has_igv: progressItem.has_igv !== false,
                  quantity: 0,
                  totalSubtotal: 0,
                  totalTaxAmount: 0,
                  totalAmount: 0,
                  descriptions: [],
                  progressIds: [],
                  // Usar el precio unitario del primer item
                  unit_price: parseFloat(progressItem.unit_price || 0),
                };
              }
              
              // Sumar cantidades y totales
              groupedBySku[groupKey].quantity += parseFloat(progressItem.quantity || 0);
              groupedBySku[groupKey].totalSubtotal += parseFloat(progressItem.subtotal || 0);
              groupedBySku[groupKey].totalTaxAmount += parseFloat(progressItem.tax_amount || 0);
              groupedBySku[groupKey].totalAmount += parseFloat(progressItem.total || 0);
              
              if (progressItem.description) {
                groupedBySku[groupKey].descriptions.push(progressItem.description);
              }
              
              groupedBySku[groupKey].progressIds.push(progressItem.id);
            });
            
            // Convertir los grupos en items del documento
            // Cada grupo representa un SKU + proyecto único, así que cada uno será un item separado
            console.log('Grupos por SKU antes de convertir:', groupedBySku);
            Object.values(groupedBySku).forEach(group => {
              // Verificar si ya existe un item con el mismo service_id (mismo SKU), project_id y has_igv
              // Si tienen el mismo SKU pero proyectos distintos, crear items separados
              const existingItemIndex = newItems.findIndex(i => {
                if (i.item_type !== 'service') return false;
                // Debe coincidir en service_id (SKU), project_id Y has_igv
                if (i.service_id !== group.service_id.toString()) return false;
                if (i.project_id !== (group.project_id || '').toString()) return false;
                if (i.has_igv !== group.has_igv) return false;
                // Solo items que vienen de servicios continuos
                if (!i.continuous_service_progress_id && !i.continuous_service_progress_ids) return false;
                return true;
              });
              
              if (existingItemIndex !== -1) {
                // Si existe, sumar las cantidades y totales
                const existingItem = newItems[existingItemIndex];
                existingItem.quantity = parseFloat(existingItem.quantity || 0) + group.quantity;
                existingItem.subtotal = roundTo4(parseFloat(existingItem.subtotal || 0) + group.totalSubtotal);
                existingItem.tax_amount = roundTo4(parseFloat(existingItem.tax_amount || 0) + group.totalTaxAmount);
                existingItem.total = roundTo4(parseFloat(existingItem.total || 0) + group.totalAmount);
                // Recalcular precio unitario promedio (4 decimales)
                if (existingItem.quantity > 0) {
                  existingItem.unit_price = roundTo4(existingItem.subtotal / existingItem.quantity);
                }
                // No actualizar descripción: la pondrá el usuario
                // Agregar los IDs de progreso
                if (!existingItem.continuous_service_progress_ids) {
                  existingItem.continuous_service_progress_ids = [existingItem.continuous_service_progress_id].filter(Boolean);
                }
                existingItem.continuous_service_progress_ids.push(...group.progressIds);
              } else {
                // Si no existe, crear nuevo item (descripción vacía: la pondrá el usuario)
                const sub = roundTo4(group.totalSubtotal);
                const taxAmt = group.has_igv ? roundTo4(sub * ((updatedFormData.igv_rate ?? 18) / 100)) : 0;
                newItems.push({
                  item_type: 'service',
                  service_id: group.service_id || '',
                  project_id: group.project_id || '',
                  description: '',
                  quantity: group.quantity,
                  unit_price: group.quantity > 0 ? roundTo4(group.totalSubtotal / group.quantity) : (group.unit_price || 0),
                  has_igv: group.has_igv,
                  subtotal: sub,
                  tax_rate: group.has_igv ? (updatedFormData.igv_rate ?? 18) : 0,
                  tax_amount: taxAmt,
                  total: roundTo4(sub + taxAmt),
                  continuous_service_progress_id: group.progressIds[0], // Guardar el primer ID para referencia
                  continuous_service_progress_ids: group.progressIds, // Guardar todos los IDs
                });
                console.log('Item agregado:', newItems[newItems.length - 1]);
              }
            });
            console.log('Items finales después de procesar servicio continuo:', newItems);
          } else {
            console.log('El servicio no tiene registros de progreso');
          }
        } else {
          console.log('Servicio continuo no encontrado');
        }
      } else {
        // Remover items del servicio deseleccionado
        const service = closedContinuousServices.find(cs => cs.id === serviceId);
        if (service && service.progress) {
          // Obtener todos los IDs de progreso de este servicio
          const progressIds = service.progress.map(p => p.id);
          
          // Remover items que tienen estos IDs de progreso
          const itemsToRemove = [];
          newItems.forEach((item, index) => {
            if (item.continuous_service_progress_id && progressIds.includes(item.continuous_service_progress_id)) {
              itemsToRemove.push(index);
            } else if (item.continuous_service_progress_ids) {
              // Si tiene múltiples IDs, verificar si alguno coincide
              const hasMatchingId = item.continuous_service_progress_ids.some(id => progressIds.includes(id));
              if (hasMatchingId) {
                // Si todos los IDs están en progressIds, eliminar el item
                // Si solo algunos, actualizar el item restando los valores
                const matchingIds = item.continuous_service_progress_ids.filter(id => progressIds.includes(id));
                if (matchingIds.length === item.continuous_service_progress_ids.length) {
                  // Todos los IDs coinciden, eliminar el item
                  itemsToRemove.push(index);
                } else {
                  // Solo algunos coinciden, recalcular el item
                  // Obtener los registros de progreso que se están removiendo
                  const progressToRemove = service.progress.filter(p => matchingIds.includes(p.id));
                  const quantityToRemove = progressToRemove.reduce((sum, p) => sum + parseFloat(p.quantity || 0), 0);
                  const subtotalToRemove = progressToRemove.reduce((sum, p) => sum + parseFloat(p.subtotal || 0), 0);
                  const taxAmountToRemove = progressToRemove.reduce((sum, p) => sum + parseFloat(p.tax_amount || 0), 0);
                  const totalToRemove = progressToRemove.reduce((sum, p) => sum + parseFloat(p.total || 0), 0);
                  
                  item.quantity = Math.max(0, parseFloat(item.quantity || 0) - quantityToRemove);
                  item.subtotal = Math.max(0, parseFloat(item.subtotal || 0) - subtotalToRemove);
                  item.tax_amount = Math.max(0, parseFloat(item.tax_amount || 0) - taxAmountToRemove);
                  item.total = Math.max(0, parseFloat(item.total || 0) - totalToRemove);
                  
                  // Actualizar precio unitario (4 decimales)
                  if (item.quantity > 0) {
                    item.unit_price = roundTo4(item.subtotal / item.quantity);
                  }
                  
                  // Remover los IDs de progreso
                  item.continuous_service_progress_ids = item.continuous_service_progress_ids.filter(id => !progressIds.includes(id));
                  if (item.continuous_service_progress_ids.length > 0) {
                    item.continuous_service_progress_id = item.continuous_service_progress_ids[0];
                  } else {
                    itemsToRemove.push(index);
                  }
                }
              }
            }
          });
          
          // Remover items en orden inverso para no afectar los índices
          itemsToRemove.sort((a, b) => b - a).forEach(index => {
            newItems.splice(index, 1);
          });
        }
      }
      
      return {
        ...updatedFormData,
        selected_continuous_services: newSelected,
        items: newItems,
      };
    });
  };

  // Seleccionar guías de entrada
  const handleSelectEntryGuide = (guideId, checked) => {
    setEntryGuidesSelectionTouched(true);
    setAmountOverride(null);
    setFormData(prev => {
      const newSelected = checked
        ? [...(prev.selected_entry_guides || []), guideId]
        : (prev.selected_entry_guides || []).filter(id => id !== guideId);
      
      // Agregar items de las guías seleccionadas
      const newItems = [...prev.items];
      let updatedFormData = { ...prev };
      
      if (checked) {
        const guide = allPendingEntryGuides.find((eg) => eg.id === guideId);
        if (guide) {
          // Si no hay proveedor seleccionado, usar el de la guía
          if (!prev.supplier_id && guide.supplier_id) {
            updatedFormData.supplier_id = guide.supplier_id.toString();
          }
          
          // Si no hay fecha de entrega, usar la fecha de la guía
          if (!prev.delivery_date && guide.issue_date) {
            updatedFormData.delivery_date = guide.issue_date.split('T')[0]; // Extraer solo la fecha (YYYY-MM-DD)
          }
          
          if (guide.items && guide.items.length > 0) {
            guide.items
              .filter((item) => {
                const pendingQty = parseFloat(item.pending_quantity);
                const pendingTotal = parseFloat(item.pending_total);
                if (Number.isFinite(pendingQty) && pendingQty > 0.001) return true;
                if (Number.isFinite(pendingTotal) && pendingTotal > 0.01) return true;
                return !Number.isFinite(pendingQty) && !Number.isFinite(pendingTotal);
              })
              .forEach(item => {
              // Solo agregar si no está ya en los items
              if (!newItems.find(i => i.entry_guide_item_id === item.id)) {
                const rawUnit = item.unit_of_measure || item.product?.unit_of_measure || '';
                const pendingQty = parseFloat(item.pending_quantity);
                const rawQty = Number.isFinite(pendingQty) && pendingQty > 0
                  ? pendingQty
                  : parseFloat(item.quantity || 0);
                let lineItem = applySubtotalModeDisplay({
                  entry_guide_item_id: item.id,
                  entry_guide_id: guideId,
                  purchase_order_item_id: item.purchase_order_item_id || null,
                  purchase_order_id:
                    guide.purchase_order_id
                    || item.purchase_order_item?.purchase_order_id
                    || null,
                  item_type: 'product',
                  product_id: item.product_id || '',
                  product: item.product,
                  unit_of_measure: rawUnit,
                  project_id: item.project_id || '',
                  description: item.product?.name || item.description || '',
                  quantity: rawQty,
                  unit_price: parseFloat(item.unit_price || 0),
                  has_igv: item.has_igv !== undefined ? item.has_igv : true,
                  is_direct_consumption: false,
                  _source_quantity: rawQty,
                  _source_unit_of_measure: rawUnit,
                });
                const pendingGuideTotal = parseFloat(item.pending_total);
                const storedGuideTotal = parseFloat(item.total);
                const subtotal = Number.isFinite(pendingGuideTotal) && pendingGuideTotal > 0
                  && !lineItem.uses_converted_display
                  ? roundTo4(pendingGuideTotal)
                  : (Number.isFinite(storedGuideTotal) && storedGuideTotal >= 0
                    && !lineItem.uses_converted_display
                    ? roundTo4(storedGuideTotal)
                    : resolveItemLineSubtotal(lineItem));
                const docRate = updatedFormData.igv_rate ?? 18;
                const taxAmount = lineItem.has_igv !== false ? roundTo4(subtotal * (docRate / 100)) : 0;
                newItems.push({
                  ...lineItem,
                  subtotal,
                  tax_rate: lineItem.has_igv !== false ? docRate : 0,
                  tax_amount: taxAmount,
                  total: roundTo4(subtotal + taxAmount),
                });
              }
            });
          }
        }
      } else {
        // Remover items de la guía deseleccionada
        const guide = allPendingEntryGuides.find((eg) => eg.id === guideId);
        if (guide && guide.items) {
          guide.items.forEach(item => {
            const index = newItems.findIndex(i => i.entry_guide_item_id === item.id);
            if (index !== -1) {
              newItems.splice(index, 1);
            }
          });
        }
      }
      
      return {
        ...updatedFormData,
        selected_entry_guides: newSelected,
        items: newItems,
      };
    });
  };

  // Agregar producto manual: recalcular totales
  const handleAddProduct = () => {
    setAmountOverride(null);
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_type: 'product',
        product_id: '',
        service_id: '',
        unit_of_measure: '',
        project_id: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        has_igv: true,
        subtotal: 0,
        tax_rate: prev.igv_rate ?? 18,
        tax_amount: 0,
        total: 0,
        is_direct_consumption: false,
      }],
    }));
  };

  // Agregar servicio manual: recalcular totales
  const handleAddService = () => {
    setAmountOverride(null);
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_type: 'service',
        product_id: '',
        service_id: '',
        project_id: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        has_igv: true,
        subtotal: 0,
        tax_rate: prev.igv_rate ?? 18,
        tax_amount: 0,
        total: 0,
      }],
    }));
  };

  const handleOpenActivoModal = () => {
    if (!formData.supplier_id) {
      setToast({
        isOpen: true,
        message: 'Seleccione un proveedor antes de registrar un activo',
        type: 'warning',
      });
      return;
    }
    setEditingActivoIndex(null);
    setIsActivoModalOpen(true);
  };

  const handleSaveDocumentActivo = (activo) => {
    const wasEditing = editingActivoIndex != null;
    setPendingActivos((prev) => {
      if (wasEditing) {
        return prev.map((row, idx) => (idx === editingActivoIndex ? activo : row));
      }
      return [...prev, {
        ...activo,
        brand: activo.brand ?? '',
        model: activo.model ?? '',
        acquisition_quantity: activo.acquisition_quantity ?? 1,
        acquisition_unit_price: activo.acquisition_unit_price ?? '',
        status: 'pending_start',
        is_active: true,
      }];
    });
    setEditingActivoIndex(null);
    setToast({
      isOpen: true,
      message: wasEditing
        ? 'Ficha de activo actualizada.'
        : 'Activo añadido. Complete cantidad y precio en la lista de ítems.',
      type: 'info',
    });
  };

  const handlePendingActivoChange = (index, field, value) => {
    setPendingActivos((prev) =>
      prev.map((activo, idx) => (idx === index ? { ...activo, [field]: value } : activo))
    );
  };

  const suggestedActivoCode = useMemo(() => {
    const series = (formData.series || 'DOC').replace(/\s+/g, '');
    const number = (formData.number || '0').replace(/\s+/g, '');
    return `ACT-${series}-${number}-${pendingActivos.length + 1}`;
  }, [formData.series, formData.number, pendingActivos.length]);

  const documentActivoContext = useMemo(() => {
    const supplier = suppliers.find((s) => String(s.id) === String(formData.supplier_id));
    return {
      supplier_id: formData.supplier_id,
      supplier_name: supplier?.name || '',
      document_type: formData.document_type,
      series: formData.series,
      number: formData.number,
      issue_date: formData.issue_date,
      currency: formData.currency,
    };
  }, [formData, suppliers]);

  const handleDownloadItemsTemplate = () => {
    downloadDocumentItemsTemplate();
  };

  const handlePickItemsImport = () => {
    itemsImportFileRef.current?.click();
  };

  const handleItemsImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const nameOk = /\.xlsx$/i.test(file.name || '') || (file.type || '').includes('spreadsheetml');
    if (!nameOk) {
      setToast({
        isOpen: true,
        message: 'Use un archivo Excel .xlsx (misma estructura que la plantilla descargada).',
        type: 'error',
      });
      return;
    }
    if (!catalogLoaded) {
      setToast({
        isOpen: true,
        message: 'Espere a que cargue el formulario antes de importar.',
        type: 'warning',
      });
      return;
    }
    setImportingItems(true);
    try {
      const { items: parsed, errors, warnings } = await parseDocumentItemsExcel(file, {
        products,
        services,
        units,
        projects,
        igvRate: formData.igv_rate ?? 18,
        productConversionsEnabled,
      });
      if (parsed.length === 0 && !errors.length) {
        setToast({ isOpen: true, message: 'No se importó ninguna fila con datos.', type: 'warning' });
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: parsed,
        errors: errors || [],
        warnings: warnings || [],
      });
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al leer el archivo Excel.',
        type: 'error',
      });
    } finally {
      setImportingItems(false);
    }
  };

  const handleConfirmImportPreview = (confirmedRows) => {
    const source = confirmedRows || importPreview?.items || [];
    if (!source.length) {
      setImportPreview(null);
      return;
    }
    setAmountOverride(null);
    const toAdd = source.map(({ _label, _tipoLabel, _unitFormula, _unitIsCustom, ...item }) => ({
      ...item,
      product_id: item.product_id ? String(item.product_id) : '',
      service_id: item.service_id ? String(item.service_id) : '',
      project_id: item.project_id ? String(item.project_id) : '',
    }));
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, ...toAdd],
    }));
    setImportPreview(null);
    const wMsg = importPreview.warnings?.length
      ? ` Advertencias: ${importPreview.warnings.slice(0, 3).join(' ')}${importPreview.warnings.length > 3 ? '…' : ''}`
      : '';
    setToast({
      isOpen: true,
      message: `Se añadieron ${toAdd.length} fila(s) al documento.${wMsg}`,
      type: 'info',
    });
  };

  // Atajos de teclado: Ctrl+Alt+P = Agregar producto, Ctrl+Alt+S = Agregar servicio
  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName) || target?.isContentEditable) return;
      if (e.ctrlKey && e.altKey) {
        if (e.key.toLowerCase() === 'p') {
          e.preventDefault();
          if (quickProductLineEnabled) {
            stagingProductSelectorRef.current?.focus?.();
          } else {
          handleAddProduct();
          }
        } else if (e.key.toLowerCase() === 's') {
          e.preventDefault();
          handleAddService();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [quickProductLineEnabled]);

  const findPurchaseOrderForItem = (item) => {
    if (!item) return null;
    const allOrders = [...pendingPurchaseOrders, ...relatedPurchaseOrders];
    const allGuides = [...pendingEntryGuides, ...relatedEntryGuides];

    if (item.purchase_order_id) {
      const byId = allOrders.find((po) => Number(po.id) === Number(item.purchase_order_id));
      if (byId) return byId;
    }
    if (item.purchase_order_item_id) {
      const byItem = allOrders.find((po) =>
        (po.items || []).some((oi) => Number(oi.id) === Number(item.purchase_order_item_id)),
      );
      if (byItem) return byItem;
    }
    if (item.entry_guide_id) {
      const guide = allGuides.find((g) => Number(g.id) === Number(item.entry_guide_id));
      if (guide?.purchase_order) return guide.purchase_order;
      if (guide?.purchase_order_id) {
        const byGuidePo = allOrders.find((po) => Number(po.id) === Number(guide.purchase_order_id));
        if (byGuidePo) return byGuidePo;
      }
    }
    return null;
  };

  const resolveUseConvertedQuantityForSubtotal = (item) => {
    if (!productConversionsEnabled) return true;
    const order = findPurchaseOrderForItem(item);
    if (!order) return true;
    return order.use_converted_quantity_for_subtotal !== false;
  };

  // Subtotal = cantidad visible en la línea × precio unitario (sin reconvertir si hay tasa personalizada).
  const resolveItemLineSubtotal = (item) => {
    const qty = parseNumberInput(item?.quantity) || 0;
    const unitPrice = parseNumberInput(item?.unit_price) || 0;
    return roundTo4(qty * unitPrice);
  };

  const applySubtotalModeDisplay = (lineItem, { useConverted } = {}) => {
    const product =
      lineItem?.item_type === 'product' && lineItem?.product_id
        ? findProductById(lineItem.product_id, lineItem.product)
        || lineItem.product
        : null;
    const sourceUnit = lineItem._source_unit_of_measure ?? lineItem.unit_of_measure ?? '';
    const sourceQty = lineItem._source_quantity ?? lineItem.quantity ?? 0;
    const useConvertedFlag = useConverted ?? resolveUseConvertedQuantityForSubtotal(lineItem);
    const display = applyConvertedLineDisplay({
      product,
      unitCode: sourceUnit,
      quantity: sourceQty,
      productConversionsEnabled,
      useConvertedQuantityForSubtotal: useConvertedFlag,
    });
    return {
      ...lineItem,
      product,
      _source_quantity: sourceQty,
      _source_unit_of_measure: sourceUnit,
      quantity: display.quantity,
      unit_of_measure: display.unitCode,
      uses_converted_display: display.usesConvertedDisplay,
    };
  };

  const recalcDocumentProductTotals = (item, igvRate) => {
    const subtotal = resolveItemLineSubtotal(item);
    const hasIgv = item.has_igv !== undefined ? item.has_igv : true;
    const taxRate = hasIgv ? (igvRate ?? 18) : 0;
    const taxAmount = roundTo4(subtotal * (taxRate / 100));
    const total = roundTo4(subtotal + taxAmount);
    return { ...item, subtotal, tax_amount: taxAmount, total, tax_rate: taxRate };
  };

  const handleStagingChange = (field, value) => {
    setStagingError('');
    setStagingItem((prev) => {
      let updated = { ...prev };
      if (field === 'product_id' && value) {
        const product = findProductById(value);
        if (product) {
          updated.description = product.name || '';
          updated.unit_of_measure = product.unit_of_measure || '';
          updated.uses_converted_display = false;
          delete updated._source_quantity;
          delete updated._source_unit_of_measure;
          const defaults = applyProductLineDefaults(productLineSettings);
          if (defaults.project_id !== undefined) {
            updated.project_id = defaults.project_id;
          }
          if (defaults.has_igv !== undefined) {
            updated.has_igv = defaults.has_igv;
          }
        }
      }
      if (field === 'unit_of_measure') {
        updated.uses_converted_display = false;
        delete updated._source_quantity;
        delete updated._source_unit_of_measure;
      }
      const rawUnitPrice = field === 'unit_price'
        ? (parseNumberInput(value) || 0)
        : (parseNumberInput(updated.unit_price) || 0);
      const unitPriceRounded = field === 'unit_price'
        ? roundPrice(rawUnitPrice)
        : roundPrice(parseNumberInput(updated.unit_price) || 0);
      const rawQuantity = field === 'quantity'
        ? (parseNumberInput(value) || 0)
        : (parseNumberInput(updated.quantity) || 0);
      const quantityNum = Number.isFinite(rawQuantity) && rawQuantity >= 0
        ? rawQuantity
        : (parseFloat(updated.quantity) || 0);
      updated = {
        ...updated,
        [field]: field === 'quantity'
          ? quantityNum
          : field === 'unit_price'
            ? unitPriceRounded
            : field === 'has_igv'
              ? value === true || value === 'true'
              : field === 'is_direct_consumption'
                ? value === true || value === 'true'
                : value,
      };
      if (field === 'quantity') updated.quantity = quantityNum;
      if (field === 'unit_price') updated.unit_price = unitPriceRounded;
      return recalcDocumentProductTotals(updated, formData.igv_rate);
    });
  };

  const handleQuickAddProduct = () => {
    if (!stagingItem.product_id) {
      setStagingError('Seleccione un producto');
      return;
    }
    const qty = parseNumberInput(stagingItem.quantity) || 0;
    if (qty <= 0) {
      setStagingError('La cantidad debe ser mayor a 0');
      return;
    }
    const price = parseNumberInput(stagingItem.unit_price) || 0;
    if (price <= 0) {
      setStagingError('El precio unitario debe ser mayor a 0');
      return;
    }
    setAmountOverride(null);
    const itemToAdd = recalcDocumentProductTotals(
      { ...stagingItem, tax_rate: formData.igv_rate ?? 18 },
      formData.igv_rate,
    );
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, itemToAdd],
    }));
    setStagingItem(emptyStagingWithDefaults(emptyDocumentProductStagingItem, productLineSettings, formData.igv_rate ?? 18));
    setStagingError('');
    window.setTimeout(() => stagingProductSelectorRef.current?.focus?.(), 0);
  };

  const handleItemChange = (index, field, value) => {
    setAmountOverride(null);
    setFormData(prev => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      
      // Si se cambia el producto, actualizar la descripción y unidad automáticamente
      if (field === 'product_id' && value) {
        const product = findProductById(value);
        if (product) {
          currentItem.description = product.name || '';
          currentItem.unit_of_measure = product.unit_of_measure || '';
          currentItem.uses_converted_display = false;
          delete currentItem._source_quantity;
          delete currentItem._source_unit_of_measure;
          const defaults = applyProductLineDefaults(productLineSettings);
          if (defaults.project_id !== undefined) {
            currentItem.project_id = defaults.project_id;
          }
          if (defaults.has_igv !== undefined) {
            currentItem.has_igv = defaults.has_igv;
          }
        }
      }

      if (field === 'unit_of_measure') {
        currentItem.uses_converted_display = false;
        delete currentItem._source_quantity;
        delete currentItem._source_unit_of_measure;
      }
      
      // Si se cambia el servicio, actualizar la descripción automáticamente
      if (field === 'service_id' && value) {
        const service = services.find(s => s.id === parseInt(value));
        if (service) {
          currentItem.description = service.name || '';
        }
      }
      
      // Precio unitario: siempre neto (la palanca Con IGV se resuelve en UnitPriceTextInput)
      const rawUnitPrice = field === 'unit_price' ? (parseNumberInput(value) || 0) : (parseNumberInput(currentItem.unit_price) || 0);
      const unitPriceRounded = field === 'unit_price'
        ? roundPrice(rawUnitPrice)
        : roundPrice(parseNumberInput(currentItem.unit_price) || 0);
      const rawQuantity = field === 'quantity' ? (parseNumberInput(value) || 0) : (parseNumberInput(currentItem.quantity) || 0);
      const quantityNum = Number.isFinite(rawQuantity) && rawQuantity >= 0 ? rawQuantity : (parseFloat(currentItem.quantity) || 0);
      const updatedItem = {
        ...currentItem,
        [field]: field === 'quantity' || field === 'tax_rate'
          ? quantityNum
          : field === 'unit_price'
          ? unitPriceRounded
          : field === 'has_igv'
          ? value === true || value === 'true'
          : value,
      };
      if (field === 'quantity') updatedItem.quantity = quantityNum;
      updatedItem.unit_price = field === 'unit_price' ? unitPriceRounded : (Math.round((parseNumberInput(currentItem.unit_price) || 0) * 10000) / 10000);
      updatedItem.quantity = field === 'quantity' ? quantityNum : (Number(currentItem.quantity) || 0);

      // Recalcular subtotal = cantidad visible × precio unitario
      const quantity = Number(updatedItem.quantity) || 0;
      const unitPrice = Number(updatedItem.unit_price) || 0;
      const subtotal = resolveItemLineSubtotal(updatedItem);
      const hasIgv = updatedItem.has_igv !== undefined ? updatedItem.has_igv : true;
      const docIgvRate = prev.igv_rate ?? 18;
      const taxRate = hasIgv ? docIgvRate : 0;
      const taxAmount = roundTo4(subtotal * (taxRate / 100));
      const total = roundTo4(subtotal + taxAmount);

      updatedItem.subtotal = subtotal;
      updatedItem.tax_amount = taxAmount;
      updatedItem.total = total;

      // Log cuando se modifica cantidad o precio unitario para depurar el cálculo del subtotal
      if (field === 'quantity' || field === 'unit_price') {
        console.log('[DocumentForm] handleItemChange → recalculo subtotal', {
          campo: field,
          valorRecibido: value,
          cantidad: quantity,
          precioUnitario: unitPrice,
          subtotal: quantity + ' × ' + unitPrice + ' = ' + subtotal,
          tax_amount: taxAmount,
          total: total,
          itemIndex: index,
        });
      }

      newItems[index] = updatedItem;
      return {
        ...prev,
        items: newItems,
      };
    });
  };

  // Remover item: recalcular totales automáticamente
  const handleClosePurchaseOrderFulfillment = async () => {
    const orderId = closePoConfirm.orderId;
    if (!orderId) return;
    setClosingPurchaseOrder(true);
    try {
      const res = await purchaseOrderService.closeFulfillment(orderId, {
        notes: 'Cerrada manualmente: el documento no coincide con la orden de compra.',
        document_id: document?.id ? Number(document.id) : null,
      });
      if (res.success) {
        setPendingPurchaseOrders((prev) =>
          prev.filter((o) => Number(o.id) !== Number(orderId)),
        );
        setFormData((prev) => ({
          ...prev,
          selected_purchase_orders: prev.selected_purchase_orders.filter(
            (id) => Number(id) !== Number(orderId),
          ),
        }));
        setToast({
          isOpen: true,
          message: res.message || `Orden ${closePoConfirm.code} marcada como completada`,
          type: 'success',
        });
        setClosePoConfirm({ open: false, orderId: null, code: '' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message:
          error.response?.data?.message || 'No se pudo cerrar la orden de compra',
        type: 'error',
      });
    } finally {
      setClosingPurchaseOrder(false);
    }
  };

  const handleRemoveItem = (index) => {
    setAmountOverride(null);
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
    setPendingMachineryEvents(prev => prev.filter(e => e.itemIndex !== index).map(e => ({ ...e, itemIndex: e.itemIndex > index ? e.itemIndex - 1 : e.itemIndex })));
    setLastEventFormByItem(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = parseInt(k, 10);
        if (i === index) return;
        next[i > index ? i - 1 : i] = v;
      });
      return next;
    });
  };

  // Calcular totales
  const calculateTotal = () => {
    const IGV_RATE = (formData.igv_rate ?? 18) / 100;
    
    let baseImponible = 0; // Items con IGV
    let noGravados = 0; // Items sin IGV
    
    formData.items.forEach(item => {
      const subtotal = resolveItemLineSubtotal(item);
      const hasIgv = item.has_igv !== undefined ? item.has_igv : true;
      
      if (!isNaN(subtotal) && isFinite(subtotal)) {
        if (hasIgv) {
          baseImponible += subtotal;
        } else {
          noGravados += subtotal;
        }
      }
    });

    if (activosEnabled && pendingActivos.length > 0) {
      pendingActivos.forEach((activo) => {
        const qty = parseNumberInput(activo.acquisition_quantity) || 0;
        const price = parseNumberInput(activo.acquisition_unit_price) || 0;
        const subtotal = roundTo4(qty * price);
        if (!isNaN(subtotal) && isFinite(subtotal) && subtotal > 0) {
          baseImponible += subtotal;
        }
      });
    }
    
    baseImponible = isNaN(baseImponible) || !isFinite(baseImponible) ? 0 : baseImponible;
    noGravados = isNaN(noGravados) || !isFinite(noGravados) ? 0 : noGravados;
    
    const igv = baseImponible * IGV_RATE;
    const total = baseImponible + igv + noGravados;
    
    const validIgv = isNaN(igv) || !isFinite(igv) ? 0 : igv;
    const validTotal = isNaN(total) || !isFinite(total) ? 0 : total;
    
    return {
      baseImponible: roundTo4(baseImponible),
      igv: roundTo4(validIgv),
      noGravados: roundTo4(noGravados),
      total: roundTo4(validTotal)
    };
  };

  // Función helper para convertir fecha string a Date en horario de Lima
  const parseDateInLima = (dateString) => {
    if (!dateString) return null;
    
    // Si es string en formato YYYY-MM-DD, parsearlo correctamente en horario de Lima
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      // Crear fecha directamente usando los componentes locales (no UTC)
      // Esto evita problemas de conversión de zona horaria
      const dateInLima = new Date(year, month - 1, day, 12, 0, 0);
      // Ajustar a medianoche en horario local (que es Lima)
      dateInLima.setHours(0, 0, 0, 0);
      return dateInLima;
    }
    
    // Si ya es un Date object, usarlo directamente
    if (dateString instanceof Date) {
      return dateString;
    }
    
    // Fallback: crear Date normal
    const fallbackDate = new Date(dateString);
    // Ajustar a medianoche en horario local
    fallbackDate.setHours(0, 0, 0, 0);
    return fallbackDate;
  };

  // Calcular fecha de pago basándose en la condición seleccionada
  const calculatePaymentDate = () => {
    if (!formData.issue_date) return null;
    
    // Parsear fecha de emisión en horario de Lima
    const issueDate = parseDateInLima(formData.issue_date);
    if (!issueDate) return null;
    
    if (formData.payment_condition_type === 'cash') {
      // Al contado: la fecha de pago es la misma que la fecha de emisión
      return issueDate;
    } else if (formData.payment_condition_type === 'days') {
      const days = parseInt(formData.payment_condition_days) || 0;
      const paymentDate = new Date(issueDate);
      paymentDate.setDate(paymentDate.getDate() + days);
      return paymentDate;
    } else if (formData.payment_condition_type === 'date' && formData.payment_condition_date) {
      return parseDateInLima(formData.payment_condition_date);
    }
    
    return null;
  };

  // Calcular días hasta la fecha de pago
  const calculateDaysUntilPayment = () => {
    const paymentDate = calculatePaymentDate();
    if (!paymentDate || !formData.issue_date) return null;
    
    // Parsear fecha de emisión en horario de Lima
    const issueDate = parseDateInLima(formData.issue_date);
    if (!issueDate) return null;
    
    // Calcular diferencia en días usando fechas en horario de Lima
    const diffTime = paymentDate - issueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.supplier_id) {
      newErrors.supplier_id = 'El proveedor es requerido';
    }

    if (!formData.issue_date) {
      newErrors.issue_date = 'La fecha de emisión es requerida';
    }

    const hasActivos = activosEnabled && pendingActivos.length > 0;
    const activosOnly = hasActivos && formData.items.length === 0;

    // Validar que exista fecha de entrega o fechas de servicio (no aplica si el documento es solo activos)
    const hasDeliveryDate = formData.delivery_date && formData.delivery_date.trim() !== '';
    const hasServiceDates = formData.service_start_date && formData.service_start_date.trim() !== '' && 
                           formData.service_end_date && formData.service_end_date.trim() !== '';
    
    if (!activosOnly && !hasDeliveryDate && !hasServiceDates) {
      newErrors.delivery_date = 'Debe ingresar la fecha de entrega o las fechas de servicio';
      newErrors.service_start_date = 'Debe ingresar la fecha de entrega o las fechas de servicio';
    }

    if (formData.items.length === 0 && !hasActivos) {
      newErrors.items = activosEnabled
        ? 'Debe agregar al menos un ítem o registrar un activo en el documento'
        : 'Debe agregar al menos un item al documento';
    }

    formData.items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`items.${index}.description`] = 'La descripción es requerida';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`items.${index}.quantity`] = 'La cantidad debe ser mayor a 0';
      }
      if (!item.unit_price || item.unit_price <= 0) {
        newErrors[`items.${index}.unit_price`] = 'El precio unitario debe ser mayor a 0';
      }
    });

    if (activosEnabled && pendingActivos.length > 0) {
      pendingActivos.forEach((activo, index) => {
        const qty = parseFloat(activo.acquisition_quantity) || 0;
        const price = parseFloat(activo.acquisition_unit_price) || 0;
        if (qty <= 0) {
          newErrors[`activos.${index}.quantity`] = 'La cantidad debe ser mayor a 0';
        }
        if (price <= 0) {
          newErrors[`activos.${index}.unit_price`] = 'El precio unitario debe ser mayor a 0';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (readOnly) {
      return;
    }

    const validationErrors = validate();
    if (validationErrors) {
      const messages = [...new Set(Object.values(validationErrors).filter(Boolean))];
      setToast({
        isOpen: true,
        message: messages.length > 0 ? messages.slice(0, 2).join(' · ') : 'Por favor, corrige los errores en el formulario',
        type: 'error'
      });
      return;
    }

    // Si es edición, mostrar modal de confirmación antes de guardar
    if (document?.id) {
      setUpdateEntryGuideWithDocument(false);
      setIsConfirmEditModalOpen(true);
      return;
    }

    // Si es creación (no edición), mostrar modal de confirmación
    if (!document || !document.id) {
      const offerGuide = shouldOfferEntryGuideOnCreate();
      setCreateEntryGuideWithDocument(offerGuide);
      if (offerGuide) {
        if (entryGuideWarehouses.length === 1) {
          setEntryGuideWarehouseId(String(entryGuideWarehouses[0].id));
        }
        if (!entryGuideTime) {
          setEntryGuideTime(getCurrentTimeLima());
        }
      }
      setIsConfirmCreateModalOpen(true);
      return;
    }
  };

  const handleConfirmEdit = async () => {
    setIsConfirmEditModalOpen(false);
    await submitDocument();
  };

  const handleConfirmCreate = async () => {
    if (shouldOfferEntryGuideOnCreate() && createEntryGuideWithDocument) {
      if (!entryGuideWarehouseId) {
        setToast({
          isOpen: true,
          message: 'Selecciona el almacén para la guía de entrada',
          type: 'error',
        });
        return;
      }
      if (!entryGuideTime || !entryGuideTime.trim()) {
        setToast({
          isOpen: true,
          message: 'Ingresa la hora de ingreso para la guía',
          type: 'error',
        });
        return;
      }
    }
    setIsConfirmCreateModalOpen(false);
    await submitDocument();
  };

  const submitDocument = async () => {
    setLoading(true);

    try {
      const calculated = calculateTotal();
      const totals = amountOverride || calculated;

      const paymentDate = calculatePaymentDate();

      // Al actualizar documento: enviar lista completa de eventos (pendientes editados + cargados sin tocar) para que el reporte quede sincronizado
      let machineryEventsPayload = [];
      if (document?.id) {
        const itemIndexesWithPending = new Set(pendingMachineryEvents.map((e) => e.itemIndex));
        const itemIndexesWithLoaded = new Set((loadedMachineryEventsByItem || []).map((e) => e.itemIndex));
        const allIndexes = new Set([...itemIndexesWithPending, ...itemIndexesWithLoaded]);
        for (const itemIndex of allIndexes) {
          const pending = pendingMachineryEvents.find((e) => e.itemIndex === itemIndex);
          if (pending) {
            const { itemIndex: _i, displaySummary: _s, ...ev } = pending;
            machineryEventsPayload.push({ ...ev, item_index: _i });
          } else {
            const form = lastEventFormByItem[itemIndex];
            if (form) {
              machineryEventsPayload.push({
                event_date: form.event_date || null,
                event_time: form.event_time || null,
                machine_id: form.machine_id || null,
                vehicle_id: form.vehicle_id || null,
                event_type: form.event_type || null,
                operator: form.operator || null,
                hours: form.hours !== '' && form.hours != null ? form.hours : null,
                kilometers: form.kilometers !== '' && form.kilometers != null ? form.kilometers : null,
                project_id: form.project_id && form.project_id !== '' ? form.project_id : null,
                notes: form.notes || null,
                item_index: itemIndex,
              });
            }
          }
        }
      } else if (pendingMachineryEvents.length > 0) {
        machineryEventsPayload = pendingMachineryEvents.map(({ itemIndex, displaySummary, ...ev }) => ({ ...ev, item_index: itemIndex }));
      }

      const hasActivosOnly = activosEnabled && pendingActivos.length > 0 && formData.items.length === 0;
      const resolvedDeliveryDate = hasActivosOnly ? null : (formData.delivery_date || null);

      const submitData = {
        supplier_id: parseInt(formData.supplier_id),
        document_type: formData.document_type,
        series: formData.series || null,
        number: formData.number || null,
        issue_date: formData.issue_date,
        due_date: formData.due_date || null,
        currency: formData.currency,
        payment_condition_type: formData.payment_condition_type,
        payment_condition_days: formData.payment_condition_type === 'days' ? parseInt(formData.payment_condition_days) : null,
        payment_condition_date: formData.payment_condition_type === 'date' ? formData.payment_condition_date : null,
        reference_document_type: formData.reference_document_type || null,
        reference_document_date: formData.reference_document_date || null,
        reference_series: formData.reference_series || null,
        reference_number: formData.reference_number || null,
        service_start_date: formData.service_start_date || null,
        service_end_date: formData.service_end_date || null,
        delivery_date: resolvedDeliveryDate,
        notes: formData.notes || null,
        igv_rate: formData.igv_rate ?? 18,
        purchase_order_ids: formData.selected_purchase_orders.length > 0 ? formData.selected_purchase_orders.map(id => parseInt(id)) : [],
        service_order_ids: formData.selected_service_orders.length > 0 ? formData.selected_service_orders.map(id => parseInt(id)) : [],
        continuous_service_ids: formData.selected_continuous_services && formData.selected_continuous_services.length > 0 ? formData.selected_continuous_services.map(id => parseInt(id)) : [],
        ...(document?.id && !entryGuidesSelectionTouched
          ? {}
          : {
            entry_guide_ids: resolveEntryGuideIdsForSubmit(formData.selected_entry_guides),
            entry_guide_selection_modified: entryGuidesSelectionTouched,
          }),
        ...(!document?.id && createEntryGuideWithDocument && shouldOfferEntryGuideOnCreate()
          ? {
            create_entry_guide: {
              warehouse_id: parseInt(entryGuideWarehouseId, 10),
              entry_time: entryGuideTime.trim().slice(0, 5),
              entry_date: resolveEntryGuideDateFromDocument(),
            },
          }
          : {}),
        ...(document?.id && updateEntryGuideWithDocument && shouldOfferEntryGuideOnUpdate()
          ? { update_entry_guide: true }
          : {}),
        requirement_ids:
          formData.selected_requirements.length > 0
            ? formData.selected_requirements.map((id) => parseInt(id, 10))
            : [],
        ...(machineryEventsPayload.length > 0 || document?.id ? { machinery_events: machineryEventsPayload } : {}),
        ...(activosEnabled
          ? {
              activos: pendingActivos.map((a) => ({
                id: a.id || null,
                code: a.code,
                name: a.name,
                description: a.description || null,
                activo_family_id: a.activo_family_id,
                area_id: a.area_id,
                brand: a.brand?.trim() || null,
                model: a.model?.trim() || null,
                serial_number: a.serial_number || null,
                status: 'pending_start',
                notes: a.notes || null,
                is_active: true,
                acquisition_quantity: parseFloat(a.acquisition_quantity) || 1,
                acquisition_unit_price:
                  a.acquisition_unit_price !== '' && a.acquisition_unit_price != null
                    ? parseFloat(a.acquisition_unit_price)
                    : null,
              })),
            }
          : {}),
        base_imponible: totals.baseImponible,
        igv_amount: totals.igv,
        no_gravados: totals.noGravados,
        total_amount: totals.total,
        items: formData.items.map(item => ({
          item_type: item.item_type,
          is_direct_consumption: item.item_type === 'product' ? (item.is_direct_consumption ?? false) : false,
          product_id: item.item_type === 'product' && item.product_id ? parseInt(item.product_id) : null,
          service_id: item.item_type === 'service' && item.service_id ? parseInt(item.service_id) : null,
          description: item.description || null,
          unit_of_measure: item.unit_of_measure || null,
          project_id: item.project_id && item.project_id !== '' ? parseInt(item.project_id) : null,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          has_igv: item.has_igv !== undefined ? item.has_igv : true,
          purchase_order_item_id: item.purchase_order_item_id || null,
          requirement_item_id: item.requirement_item_id || null,
          service_order_item_id: item.service_order_item_id || null,
          entry_guide_item_id: item.entry_guide_item_id || null,
          continuous_service_progress_id: item.continuous_service_progress_id || null,
          continuous_service_progress_ids: (() => {
            const ids = (item.continuous_service_progress_ids && item.continuous_service_progress_ids.length)
              ? item.continuous_service_progress_ids
              : (item.continuous_service_progress_id ? [item.continuous_service_progress_id] : []);
            return ids.length ? ids.map(id => parseInt(id, 10)) : null;
          })(),
        })),
        ...(purchaseSiteId && !document?.id ? { purchase_site_id: purchaseSiteId } : {}),
      };

      const activosRegistrados = pendingActivos.length;
      let response;
      if (document && document.id) {
        response = await documentService.updateDocument(document.id, submitData);
        const baseMessage = response.message
          || (activosRegistrados > 0
            ? `Documento actualizado. ${activosRegistrados} activo(s) registrado(s).`
            : 'Documento actualizado exitosamente');
        setToast({
          isOpen: true,
          message: formatGuideSaveMessage(baseMessage, response),
          type: 'success'
        });
      } else {
        response = await documentService.createDocument(submitData);
        const baseMessage = response.message
          || (activosRegistrados > 0
            ? `Documento creado. ${activosRegistrados} activo(s) registrado(s).`
            : 'Documento creado exitosamente');
        setToast({
          isOpen: true,
          message: formatGuideSaveMessage(baseMessage, response),
          type: 'success',
        });
      }

      if (response.success) {
        documentFormDataManager.reset();
        documentFormReferencesManager.reset();
        hasLoadedFormData.current = false;
        setPendingMachineryEvents([]);
        setPendingActivos([]);
        setEntryGuideWarehouseId('');
        setEntryGuideTime('');
        setCreateEntryGuideWithDocument(false);
        setUpdateEntryGuideWithDocument(false);
        
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(response.data);
          }
          if (onClose) {
            onClose();
          }
          if (isPageMode) {
            window.location.href = '/documents';
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error al guardar documento:', error);
      let message = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Error al guardar el documento';
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const err = error.response.data.errors;
        const list = typeof err === 'object' ? Object.entries(err).map(([k, v]) => (Array.isArray(v) ? v.join(', ') : v)).filter(Boolean) : [];
        if (list.length) message = `Validación: ${list.join(' · ')}`;
      }
      setToast({
        isOpen: true,
        message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveEntryGuideDateFromDocument = () => {
    const rawDate = (formData.delivery_date && formData.delivery_date.trim() !== '')
      ? formData.delivery_date
      : (formData.issue_date || '');
    if (!rawDate) return getCurrentDateLima();
    return formatDateToLima(rawDate) || getCurrentDateLima();
  };

  const createConfirmModal = isConfirmCreateModalOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10050] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Confirmar creación de documento
          </h3>
          <p className="text-gray-700 mb-4">
            ¿Deseas registrar este documento?
            {shouldOfferEntryGuideOnCreate() && createEntryGuideWithDocument
              ? ' El documento y la guía de entrada se crearán juntos en una sola operación.'
              : ''}
          </p>

          {shouldOfferEntryGuideOnCreate() && (
            <div className="mb-4 rounded-lg border border-teal-100 bg-teal-50/60 p-4 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createEntryGuideWithDocument}
                  onChange={(e) => setCreateEntryGuideWithDocument(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-800">
                  Crear guía de entrada al mismo tiempo (solo productos que ingresan a almacén)
                </span>
              </label>

              {createEntryGuideWithDocument && (
                <>
                  <p className="text-xs text-teal-700">
                    Fecha de la guía: {formatDateLocaleLima(resolveEntryGuideDateFromDocument())}
                  </p>
                  <div>
                    <label htmlFor="confirm-entry-guide-warehouse" className="block text-sm font-medium text-gray-700 mb-1">
                      Almacén <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="confirm-entry-guide-warehouse"
                      value={entryGuideWarehouseId}
                      onChange={(e) => setEntryGuideWarehouseId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      disabled={entryGuideWarehouses.length === 0}
                    >
                      {entryGuideWarehouses.length !== 1 && (
                        <option value="">Seleccionar almacén</option>
                      )}
                      {entryGuideWarehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.code} - {warehouse.name}
                        </option>
                      ))}
                    </select>
                    {entryGuideWarehouses.length === 0 && (
                      <p className="mt-1 text-sm text-amber-600">
                        No tiene almacenes asignados. Contacte al administrador.
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="confirm-entry-guide-time" className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de ingreso <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      id="confirm-entry-guide-time"
                      value={entryGuideTime}
                      onChange={(e) => setEntryGuideTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsConfirmCreateModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmCreate}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
            >
              {loading
                ? 'Guardando...'
                : (shouldOfferEntryGuideOnCreate() && createEntryGuideWithDocument
                  ? 'Crear documento y guía'
                  : 'Sí, crear')}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const editConfirmModal = isConfirmEditModalOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10050] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Confirmar cambios en el documento
          </h3>
          <p className="text-gray-700 mb-4">
            ¿Deseas guardar los cambios realizados en este documento?
          </p>

          {hasLinkedEntryGuidesOnEdit() && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div className="text-sm text-amber-900 space-y-2">
                  <p className="font-semibold">Precaución: una guía puede estar vinculada a más de un documento</p>
                  <p>
                    Es posible que la misma guía de entrada haya generado dos o más documentos de compra
                    (facturación parcial o en partes). Si actualizas la guía desde aquí, podrías afectar
                    cantidades, costos o saldos de los otros documentos relacionados.
                  </p>
                  {getSharedEntryGuideWarningDetails() && (
                    <p className="text-xs text-amber-800">
                      Guías detectadas: {getSharedEntryGuideWarningDetails()}
                    </p>
                  )}
                  <p className="font-medium">
                    Revise bien antes de marcar la opción de actualizar la guía.
                  </p>
                </div>
              </div>
            </div>
          )}

          {shouldOfferEntryGuideOnUpdate() && (
            <div className="mb-4 rounded-lg border border-teal-100 bg-teal-50/60 p-4 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={updateEntryGuideWithDocument}
                  onChange={(e) => setUpdateEntryGuideWithDocument(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-800">
                  También actualizar la guía de entrada generada con este documento
                  (cantidades, precios y kardex)
                </span>
              </label>
              {updateEntryGuideWithDocument && (
                <p className="text-xs text-teal-700">
                  Guía(s): {resolveWarehouseFulfillmentGuidesForEdit().map((g) => g.code).join(', ')}
                  {hasSharedEntryGuideRisk() && (
                    <span className="block mt-1 text-amber-700 font-medium">
                      Esta guía también tiene relación con otros documentos. Proceda solo si está seguro.
                    </span>
                  )}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsConfirmEditModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmEdit}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
            >
              {loading
                ? 'Guardando...'
                : (updateEntryGuideWithDocument && shouldOfferEntryGuideOnUpdate()
                  ? 'Guardar documento y guía'
                  : 'Sí, guardar cambios')}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Calcular progreso basado en órdenes pendientes
  const calculateProgress = () => {
    const selectedPOs = pendingPurchaseOrders.filter(po => 
      formData.selected_purchase_orders.includes(po.id)
    );
    const selectedSOs = pendingServiceOrders.filter(so => 
      formData.selected_service_orders.includes(so.id)
    );

    let totalItems = 0;
    let completedItems = 0;

    selectedPOs.forEach(po => {
      po.items?.forEach(item => {
        totalItems++;
        if ((item.received_quantity || 0) >= item.quantity) {
          completedItems++;
        }
      });
    });

    selectedSOs.forEach(so => {
      so.items?.forEach(item => {
        totalItems++;
        // Para servicios, considerar completado si tiene algún indicador
        // Por ahora, asumimos que está en progreso
        completedItems += 0.5;
      });
    });

    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const progress = calculateProgress();

  const handleFormKeyDown = (e) => {
    handlePreventEnterFormSubmit(e, {
      onEnterInZone: quickProductLineEnabled ? () => handleQuickAddProduct() : undefined,
    });
  };

  const formContent = (
    <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className={isPageMode ? "p-8" : "px-6 py-4"}>
      <fieldset disabled={readOnly} className={readOnly ? 'opacity-95' : undefined}>
      {errors.submit && (
        <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {errors.submit}
        </div>
      )}

      <div className="space-y-6">
        {/* Sección: Código Preview - Solo mostrar en modo creación */}
        {!document && codePreview && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              Código (Generado automáticamente)
            </label>
            <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{codePreview}</div>
            <p className="text-xs text-gray-600 mt-2">Este código se generará automáticamente al guardar el documento</p>
          </div>
        )}

        {/* Sección: Información Básica y Condición de Pago - Lado a lado */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sección: Información Básica */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Información Básica
              </h3>
              <button
                type="button"
                onClick={openOrderSelectorModal}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Seleccionar referencias
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="supplier_id" className="block text-sm font-medium text-gray-700">
                    Proveedor <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSunatSearch}
                      disabled={sunatLoading}
                      className="flex items-center gap-1 text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                      <svg className="w-5 h-5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="lucide lucide-search-icon lucide-search">
                        <path d="m21 21-4.34-4.34"/>
                        <circle cx="11" cy="11" r="8"/>
                      </svg>
                      {sunatLoading ? 'Buscando...' : 'Buscar'}
                    </button>
                  <button
                    type="button"
                    onClick={() => setIsCreateSupplierModalOpen(true)}
                      className="flex items-center gap-1 text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                      >
                      <svg className="w-5 h-5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" class="lucide lucide-plus-icon lucide-plus">
                        <path d="M5 12h14"/>
                        <path d="M12 5v14"/>
                      </svg>
                      Nuevo
                  </button>
                  </div>
                </div>
                <SupplierSelector
                  value={formData.supplier_id}
                  onChange={(value) => handleChange({ target: { name: 'supplier_id', value } })}
                  suppliers={suppliers}
                  error={errors.supplier_id}
                />
              </div>

              <div>
                <label htmlFor="document_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Documento <span className="text-red-500">*</span>
                </label>
                <select
                  id="document_type"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Moneda
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="PEN">PEN - Sol Peruano</option>
                  <option value="USD">USD - Dólar Americano</option>
                </select>
              </div>

              <div>
                <label htmlFor="igv_rate" className="block text-sm font-medium text-gray-700 mb-2">
                  Tasa IGV
                </label>
                <select
                  id="igv_rate"
                  name="igv_rate"
                  value={String(formData.igv_rate ?? 18)}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="18">18%</option>
                  <option value="10.5">10.5%</option>
                  <option value="10">10%</option>
                </select>
              </div>

              <div>
                <label htmlFor="series" className="block text-sm font-medium text-gray-700 mb-2">
                  Serie
                </label>
                <input
                  type="text"
                  id="series"
                  name="series"
                  list="document-auto-series-options"
                  value={formData.series}
                  onChange={handleChange}
                  onBlur={() => {
                    if (!document?.id) {
                      applyAutoNumberForSeries(formData.series, formData.document_type, { force: true });
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="Ej: E001"
                />
                {!document?.id && autoNumberSeries.length > 0 && (
                  <p className="mt-1 text-xs text-teal-700">
                    Solo si elige una serie de &quot;Series autonumeradas&quot; (Compras) se completará el número. Las series F/B de ventas no aplican aquí.
                  </p>
                )}
                {!document?.id && autoNumberSeries.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Ingrese serie y número del comprobante del proveedor. La autonumeración solo aplica a series configuradas en Gestión → Series autonumeradas.
                  </p>
                )}
                <datalist id="document-auto-series-options">
                  {autoNumberSeries.map((item) => (
                    <option key={item.id} value={item.series}>
                      {item.description ? `${item.series} — ${item.description}` : item.series}
                    </option>
                  ))}
                </datalist>
              </div>

              <div>
                <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-2">
                  Número
                </label>
                <input
                  type="text"
                  id="number"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="Ej: 000001"
                />
              </div>

              <div>
                <label htmlFor="issue_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Emisión <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="issue_date"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.issue_date ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                />
                {errors.issue_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.issue_date}</p>
                )}
              </div>

              <div>
                <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  id="due_date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  min={formData.issue_date}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Sección: Condición de Pago */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Condición de Pago
          </h3>
          <div className="space-y-4">
            {/* Tipo de condición */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Condición
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payment_condition_type"
                    value="cash"
                    checked={formData.payment_condition_type === 'cash'}
                    onChange={handleChange}
                    className="mr-2 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Al contado</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payment_condition_type"
                    value="days"
                    checked={formData.payment_condition_type === 'days'}
                    onChange={handleChange}
                    className="mr-2 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Cada X días (máx. 3 meses)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payment_condition_type"
                    value="date"
                    checked={formData.payment_condition_type === 'date'}
                    onChange={handleChange}
                    className="mr-2 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Fecha específica</span>
                </label>
              </div>
            </div>

            {/* Opción: Cada X días */}
            {formData.payment_condition_type === 'days' && (
              <div>
                <label htmlFor="payment_condition_days" className="block text-sm font-medium text-gray-700 mb-2">
                  Días para pagar
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    id="payment_condition_days"
                    name="payment_condition_days"
                    value={formData.payment_condition_days}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 0;
                      const maxDays = 90; // 3 meses máximo
                      const validDays = Math.min(Math.max(1, days), maxDays);
                      setFormData(prev => ({
                        ...prev,
                        payment_condition_days: validDays,
                      }));
                    }}
                    min="1"
                    max="90"
                    className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                  <span className="text-sm text-gray-600">días (máximo 90 días / 3 meses)</span>
                </div>
              </div>
            )}

            {/* Opción: Fecha específica */}
            {formData.payment_condition_type === 'date' && (
              <div>
                <label htmlFor="payment_condition_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Pago
                </label>
                <input
                  type="date"
                  id="payment_condition_date"
                  name="payment_condition_date"
                  value={formData.payment_condition_date}
                  onChange={handleChange}
                  min={formData.issue_date}
                  className="w-full md:w-64 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                />
              </div>
            )}

            {/* Información de fecha de pago y contador de días */}
            {calculatePaymentDate() && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Fecha de Pago Calculada</div>
                    <div className="text-lg font-semibold text-teal-700">
                      {(() => {
                        const paymentDate = calculatePaymentDate();
                        if (!paymentDate) return '-';
                        // Formatear fecha en horario de Lima
                        return paymentDate.toLocaleDateString('es-PE', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          timeZone: 'America/Lima'
                        });
                      })()}
                      {formData.payment_condition_type === 'cash' && (
                        <span className="ml-2 text-sm text-teal-600 font-normal">(Al contado)</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Días hasta el pago</div>
                    <div className="text-2xl font-bold text-teal-600">
                      {calculateDaysUntilPayment() !== null ? (
                        <>
                          {calculateDaysUntilPayment()} {calculateDaysUntilPayment() === 1 ? 'día' : 'días'}
                          {formData.payment_condition_type === 'cash' && (
                            <span className="ml-2 text-sm font-normal text-teal-500">(al contado)</span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Sección: Referencias - Órdenes de Compra, Guías y Servicios Continuos (solo en modo edición) */}
        {document && (relatedPurchaseOrders.length > 0 || relatedEntryGuides.length > 0 || relatedContinuousServices.length > 0 || relatedRequirements.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Referencias
            </h3>
            <div className="flex flex-wrap gap-4">
              {/* Órdenes de Compra */}
              {relatedPurchaseOrders.map((po) => (
                <div key={po.id} className="flex-1 min-w-[280px] bg-gray-50 border border-gray-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Orden de Compra</span>
                    <span className="text-xs font-mono text-gray-600">{po.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Entidad: </span>
                      <span className="text-gray-700">{po.supplier?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      <span className="text-gray-700">{po.issue_date ? formatDateLocaleLima(po.issue_date) : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Estado: </span>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        po.status === 'issued' 
                          ? 'bg-green-50 text-green-700' 
                          : po.status === 'cancelled'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {po.status === 'issued' ? 'Emitida' : po.status === 'cancelled' ? 'Cancelada' : po.status}
                      </span>
                    </div>
                    {po.total_amount && (
                      <div>
                        <span className="text-gray-500">Total: </span>
                        <span className="text-gray-700 font-medium">
                          {po.currency || 'PEN'} {parseFloat(po.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Guías de Entrada */}
              {relatedEntryGuides.map((guide) => (
                <div key={guide.id} className="flex-1 min-w-[280px] bg-gray-50 border border-gray-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Guía</span>
                    <span className="text-xs font-mono text-gray-600">{guide.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Tipo: </span>
                      <span className="text-gray-700 capitalize">
                        {guide.guide_type === 'entrada' ? 'Entrada' :
                         guide.guide_type === 'salida' ? 'Salida' :
                         guide.guide_type === 'inicial' ? 'Inicial' :
                         guide.guide_type === 'traspaso' ? 'Traspaso' :
                         guide.guide_type === 'recepcion' ? 'Recepción' :
                         guide.guide_type}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Almacén: </span>
                      <span className="text-gray-700">{guide.warehouse?.code || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      <span className="text-gray-700">{guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Estado: </span>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        guide.status === 'issued' 
                          ? 'bg-green-50 text-green-700' 
                          : guide.status === 'cancelled'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {guide.status === 'issued' ? 'Emitida' : guide.status === 'cancelled' ? 'Cancelada' : guide.status}
                      </span>
                    </div>
                    {guide.total_amount && (
                      <div>
                        <span className="text-gray-500">Total: </span>
                        <span className="text-gray-700 font-medium">
                          {guide.currency || 'PEN'} {parseFloat(guide.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Requerimientos */}
              {relatedRequirements.map((req) => (
                <div key={req.id} className="flex-1 min-w-[280px] bg-teal-50/50 border border-teal-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-teal-800">Requerimiento</span>
                    <span className="text-xs font-mono text-teal-700">{req.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      <span className="text-gray-700">
                        {req.request_date ? formatDateLocaleLima(req.request_date) : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Avance: </span>
                      <span className="text-gray-700">{req.fulfillment_percent ?? 0}%</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Servicios Continuos */}
              {relatedContinuousServices.map((cs) => (
                <div key={cs.id} className="flex-1 min-w-[280px] bg-gray-50 border border-gray-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Servicio Continuo</span>
                    <span className="text-xs font-mono text-gray-600">{cs.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Proveedor: </span>
                      <span className="text-gray-700">{cs.supplier?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Inicio: </span>
                      <span className="text-gray-700">{cs.start_date ? formatDateLocaleLima(cs.start_date) : 'N/A'}</span>
                    </div>
                    {cs.end_date && (
                      <div>
                        <span className="text-gray-500">Fin: </span>
                        <span className="text-gray-700">{formatDateLocaleLima(cs.end_date)}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Estado: </span>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        cs.status === 'closed' 
                          ? 'bg-gray-50 text-gray-700' 
                          : cs.status === 'open'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {cs.status === 'closed' ? 'Cerrado' : cs.status === 'open' ? 'Abierto' : cs.status}
                      </span>
                    </div>
                    {cs.total_amount && (
                      <div>
                        <span className="text-gray-500">Total: </span>
                        <span className="text-gray-700 font-medium">
                          PEN {parseFloat(cs.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sección: Referencias (solo para notas de crédito y débito) */}
        {(formData.document_type === 'nota_credito' || formData.document_type === 'nota_debito') && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Referencias
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Documento de Referencia
                </label>
                <select
                  name="reference_document_type"
                  value={formData.reference_document_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="">Seleccionar</option>
                  {REFERENCE_DOCUMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha del Documento de Referencia
                </label>
                <input
                  type="date"
                  name="reference_document_date"
                  value={formData.reference_document_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Serie de Referencia
                </label>
                <input
                  type="text"
                  name="reference_series"
                  value={formData.reference_series}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="Ej: F001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Referencia
                </label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="Ej: 000001"
                />
              </div>
            </div>
          </div>
        )}

        {/* Sección: Items del Documento */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-5 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 shrink-0">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Items del Documento
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {!quickProductLineEnabled && (
              <button
                type="button"
                onClick={handleAddProduct}
                  className="shrink-0 whitespace-nowrap px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                title="Atajo: Ctrl+Alt+P"
              >
                  + Producto <kbd className="ml-1 text-[10px] opacity-90 hidden xl:inline">Ctrl+Alt+P</kbd>
              </button>
              )}
              <button
                type="button"
                onClick={handleAddService}
                className="shrink-0 whitespace-nowrap px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                title="Atajo: Ctrl+Alt+S"
              >
                + Servicio <kbd className="ml-1 text-[10px] opacity-90 hidden xl:inline">Ctrl+Alt+S</kbd>
              </button>
              {activosEnabled && (
                <button
                  type="button"
                  onClick={handleOpenActivoModal}
                  className="shrink-0 whitespace-nowrap px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                  title="Preparar activo (se registrará al guardar el documento)"
                >
                  + Activo
                </button>
              )}
              <button
                type="button"
                onClick={handleDownloadItemsTemplate}
                className="shrink-0 whitespace-nowrap px-3 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Descargar plantilla
              </button>
              <input
                ref={itemsImportFileRef}
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleItemsImportFile}
              />
              <button
                type="button"
                onClick={handlePickItemsImport}
                disabled={importingItems}
                className="shrink-0 whitespace-nowrap px-3 py-2 border border-teal-300 bg-teal-50 text-teal-900 rounded-lg hover:bg-teal-100 text-sm font-medium disabled:opacity-50"
              >
                {importingItems ? 'Importando…' : 'Importar Excel'}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <PricesIncludeIgvToggle
              value={pricesIncludeIgv}
              onChange={setPricesIncludeIgv}
              igvRate={formData.igv_rate ?? 18}
            />
          </div>

          {errors.items && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {errors.items}
            </div>
          )}

          {showProductLineDefaults && (
            <ProductLineDefaultsBar
              productsProjectEnabled={productsProjectEnabled}
              productsIgvEnabled={productsIgvEnabled}
              projects={projects}
              defaultProjectId={defaultLineProjectId}
              onDefaultProjectIdChange={setDefaultLineProjectId}
              defaultHasIgv={defaultLineHasIgv}
              onDefaultHasIgvChange={setDefaultLineHasIgv}
              className="mb-4"
            />
          )}

          {quickProductLineEnabled && (
            <QuickProductLineBar onAdd={handleQuickAddProduct} error={stagingError} rowClassName={DOC_ITEM_ROW}>
              <LineItemField label="Producto" required className={DIC.product}>
                <ProductSelector
                  ref={stagingProductSelectorRef}
                  value={stagingItem.product_id}
                  onChange={(value) => handleStagingChange('product_id', value)}
                  {...productSelectorAsyncProps}
                  compact
                />
              </LineItemField>
              <LineItemField label="Unid." className={DIC.unit}>
                <UnitSelect
                  value={stagingItem.unit_of_measure || ''}
                  onChange={(v) => handleStagingChange('unit_of_measure', v)}
                  units={units}
                  product={findProductById(stagingItem.product_id, stagingItem.product)}
                  compact
                  emptyLabel="—"
                  showCodeOnly
                />
              </LineItemField>
              <LineItemField label="Proy." className={DIC.project}>
                <select
                  value={stagingItem.project_id || ''}
                  onChange={(e) => handleStagingChange('project_id', e.target.value)}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="">Seleccionar</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.code} - {project.name}
                    </option>
                  ))}
                </select>
              </LineItemField>
              <LineItemField label="Cant." required className={DIC.qty}>
                <QuantityTextInput
                  value={stagingItem.quantity}
                  onChange={(qty) => handleStagingChange('quantity', qty)}
                  decimalPlaces={4}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="0"
                />
              </LineItemField>
              <LineItemField label="P.Unit." required className={DIC.price}>
                <div className="relative w-full">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                  <UnitPriceTextInput
                    netPrice={stagingItem.unit_price}
                    onNetPriceChange={(net) => handleStagingChange('unit_price', net)}
                    pricesIncludeIgv={pricesIncludeIgv}
                    hasIgv={stagingItem.has_igv !== undefined ? stagingItem.has_igv : true}
                    igvRate={formData.igv_rate ?? 18}
                    className="w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    placeholder="0.0000"
                  />
                </div>
              </LineItemField>
              <LineItemField label="IGV" className={DIC.igv}>
                <select
                  value={stagingItem.has_igv !== undefined ? (stagingItem.has_igv ? 'true' : 'false') : 'true'}
                  onChange={(e) => handleStagingChange('has_igv', e.target.value === 'true')}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </LineItemField>
              <LineItemField label="Ingreso" required className={DIC.intake}>
                <select
                  value={stagingItem.is_direct_consumption ? 'true' : 'false'}
                  onChange={(e) => handleStagingChange('is_direct_consumption', e.target.value === 'true')}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                >
                  <option value="false">Ingresar a Almacén</option>
                  <option value="true">Consumo Directo</option>
                </select>
              </LineItemField>
            </QuickProductLineBar>
          )}

          {purchaseOrderMismatches.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-medium text-amber-900">
                {document?.id
                  ? 'El documento no coincide con la(s) orden(es) de compra vinculada(s). Puede cerrar la orden para darla por completada si la factura ya refleja lo recibido con diferencias.'
                  : 'El documento no coincide con la(s) orden(es) de compra vinculada(s). Revise las diferencias antes de registrar el documento.'}
              </p>
              {purchaseOrderMismatches.map(({ order, reasons }) => (
                <div
                  key={order.id}
                  className={`bg-white/80 rounded-md border border-amber-100 p-3 ${
                    document?.id ? 'flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{order.code}</p>
                    <ul className="mt-1 text-xs text-amber-800 list-disc list-inside space-y-0.5">
                      {reasons.slice(0, 4).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                      {reasons.length > 4 ? (
                        <li>…y {reasons.length - 4} diferencia(s) más</li>
                      ) : null}
                    </ul>
                  </div>
                  {document?.id && (
                    <MutateGate>
                      <button
                        type="button"
                        onClick={() =>
                          setClosePoConfirm({ open: true, orderId: order.id, code: order.code })
                        }
                        className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-amber-700 hover:bg-amber-800 rounded-md"
                      >
                        Cerrar orden como completada
                      </button>
                    </MutateGate>
                  )}
                </div>
              ))}
            </div>
          )}

          {formData.items.length === 0 && (!activosEnabled || pendingActivos.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay items agregados</p>
              <p className="text-sm mt-2">
                Selecciona órdenes, agrega ítems manualmente{activosEnabled ? ', registra activos' : ''} o importa desde Excel
              </p>
            </div>
          ) : (
            <div className="space-y-3 w-full min-w-0">
              {activosEnabled && pendingActivos.map((activo, idx) => {
                const qty = parseNumberInput(activo.acquisition_quantity) || 0;
                const unitPrice = parseNumberInput(activo.acquisition_unit_price) || 0;
                const subtotal = roundTo4(qty * unitPrice);
                const currencySymbol = formData.currency === 'USD' ? '$' : 'S/';

                return (
                  <div
                    key={activo.id ? `activo-${activo.id}` : `activo-new-${idx}`}
                    className="border rounded-lg pl-3 pt-3 pb-3 pr-3 bg-indigo-50/60 border-indigo-200 w-full"
                  >
                    <div className={`${DOC_ITEM_ROW} xl:items-end`}>
                      <LineItemField label="Activo" required className={DIC.activoInfo}>
                        <div className="px-3 py-2 text-sm border border-indigo-200 rounded-md bg-white w-full min-h-[34px] flex flex-col justify-center">
                          <p className="font-medium text-gray-900 break-words text-xs sm:text-sm" title={`${activo.code} — ${activo.name}`}>
                            {activo.code} — {activo.name}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                            {activo.brand} · {activo.model}
                          </p>
                        </div>
                      </LineItemField>

                      <LineItemField
                        label="Cant."
                        required
                        className={DIC.qty}
                        hint={
                          errors[`activos.${idx}.quantity`] ? (
                            <span className="text-red-600 truncate">{errors[`activos.${idx}.quantity`]}</span>
                          ) : null
                        }
                      >
                        <input
                          type="number"
                          min="0.0001"
                          step="0.0001"
                          value={activo.acquisition_quantity ?? 1}
                          onChange={(e) => handlePendingActivoChange(idx, 'acquisition_quantity', e.target.value)}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 ${
                            errors[`activos.${idx}.quantity`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                          }`}
                        />
                      </LineItemField>

                      <LineItemField
                        label="Precio Unit."
                        required
                        className={DIC.price}
                        hint={
                          errors[`activos.${idx}.unit_price`] ? (
                            <span className="text-red-600 truncate">{errors[`activos.${idx}.unit_price`]}</span>
                          ) : null
                        }
                      >
                        <div className="relative w-full">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={activo.acquisition_unit_price ?? ''}
                            onChange={(e) => handlePendingActivoChange(idx, 'acquisition_unit_price', e.target.value)}
                            className={`w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 ${
                              errors[`activos.${idx}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                            }`}
                            placeholder="0.0000"
                          />
                        </div>
                      </LineItemField>

                      <LineItemField label="Subtotal" className={DIC.subtotal}>
                        <div
                          className="w-full h-[34px] px-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-right font-medium flex items-center justify-end truncate"
                          title={subtotal.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        >
                          {currencySymbol} {subtotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                        </div>
                      </LineItemField>

                      <LineItemField hideLabel className={DIC.actions}>
                        <div className="flex flex-col items-center gap-1 w-full">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingActivoIndex(idx);
                              setIsActivoModalOpen(true);
                            }}
                            className="h-[34px] w-full flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
                            title="Editar ficha de activo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingActivos((prev) => prev.filter((_, i) => i !== idx))}
                            className="h-[34px] w-full flex items-center justify-center bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                            title="Quitar activo"
                          >
                            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </LineItemField>
                    </div>
                    <p className="text-xs text-indigo-700/80 mt-2 break-words">
                      {findFamilyPath(activo.family)} · Área: {activo.area?.name || '—'}
                      {activo.serial_number ? ` · Serie: ${activo.serial_number}` : ''}
                    </p>
                  </div>
                );
              })}

              {formData.items.map((item, index) => {
                const isFromOrder = item.purchase_order_item_id || item.service_order_item_id;
                const isFromEntryGuide = item.entry_guide_item_id;
                const order = item.purchase_order_item_id
                  ? findPurchaseOrderForItem(item)
                  : (item.service_order_id
                    ? pendingServiceOrders.find((so) => Number(so.id) === Number(item.service_order_id))
                    : null);
                
                const product = item.product_id ? findProductById(item.product_id, item.product) : null;
                
                // Obtener el item original de la orden para saber la cantidad pendiente máxima
                let maxQuantity = null;
                if (item.purchase_order_item_id) {
                  const poOrder = findPurchaseOrderForItem(item);
                  const originalItem = poOrder?.items?.find(
                    (oi) => Number(oi.id) === Number(item.purchase_order_item_id),
                  );
                  if (originalItem) {
                    const pendingRaw = originalItem.pending_quantity ?? originalItem.quantity;
                    if (item.uses_converted_display && product) {
                      const display = applyConvertedLineDisplay({
                        product,
                        unitCode: originalItem.unit_of_measure || item._source_unit_of_measure || '',
                        quantity: pendingRaw,
                        productConversionsEnabled,
                        useConvertedQuantityForSubtotal: true,
                      });
                      maxQuantity = display.usesConvertedDisplay ? display.quantity : pendingRaw;
                    } else {
                      maxQuantity = pendingRaw;
                    }
                  }
                } else if (isFromOrder && order && order.items) {
                  const originalItem = order.items.find(oi => 
                    (item.service_order_item_id && oi.id === item.service_order_item_id),
                  );
                  if (originalItem) {
                    maxQuantity = originalItem.pending_quantity || originalItem.quantity;
                  }
                }
                
                // Cantidad editable si viene de orden y hay pendiente (> 0), incluido cuando el valor es 1
                const isQuantityEditable =
                  !isFromOrder ||
                  (maxQuantity !== null && parseFloat(maxQuantity) > 0);
                
                const service = item.item_type === 'service' && item.service_id ? services.find(s => s.id === parseInt(item.service_id)) : null;
                const subtotal = resolveItemLineSubtotal(item);
                if (index === 0 && (parseNumberInput(item.quantity) || 0) > 0 && (parseNumberInput(item.unit_price) || 0) > 0) {
                  console.log('[DocumentForm] render item subtotal', { index, quantity: item.quantity, unit_price: item.unit_price, itemSubtotal: item.subtotal, subtotal });
                }
                
                return (
                  <div key={index} className={`border rounded-lg p-3 ${isFromOrder ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    {/* Información de la orden si viene de una orden */}
                    {isFromOrder && order && (
                      <div className="mb-3 pb-3 border-b border-blue-300">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                            {item.purchase_order_item_id ? 'De Orden de Compra' : 'De Orden de Servicio'}: {order.code}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-600">Proveedor:</span>
                            <span className="ml-2 font-medium text-gray-800">{order.supplier?.name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Condición de Pago:</span>
                            <span className="ml-2 font-medium text-gray-800">
                              {order.payment_condition_type === 'cash' ? 'Al contado' :
                               order.payment_condition_type === 'days' ? `Cada ${order.payment_condition_days} días` :
                               order.payment_condition_type === 'date' ? new Date(order.payment_condition_date).toLocaleDateString('es-PE') :
                               'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className={DOC_ITEM_ROW}>
                      {item.item_type === 'product' ? (
                        <LineItemField label="Producto" required className={DIC.product}>
                          <ProductSelector
                            value={item.product_id}
                            onChange={(value) => handleItemChange(index, 'product_id', value)}
                            {...productSelectorAsyncProps}
                            error={errors[`items.${index}.product_id`]}
                            compact={true}
                          />
                        </LineItemField>
                      ) : (
                        <>
                          <LineItemField label="Servicio" required className={DIC.service}>
                            <ServiceSelector
                              value={item.service_id}
                              onChange={(value) => handleItemChange(index, 'service_id', value)}
                              services={services}
                              error={errors[`items.${index}.service_id`]}
                              compact={true}
                            />
                          </LineItemField>
                          <LineItemField label="Descr." className={DIC.description}>
                            <input
                              type="text"
                              value={item.description || ''}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                                errors[`items.${index}.description`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                              }`}
                              placeholder="Descripción del servicio"
                              disabled={isFromOrder}
                            />
                          </LineItemField>
                        </>
                      )}

                      {item.item_type === 'product' && (
                        <LineItemField label="Unid." className={DIC.unit}>
                          <UnitSelect
                            value={item.unit_of_measure || ''}
                            onChange={(v) => handleItemChange(index, 'unit_of_measure', v)}
                            units={units}
                            product={findProductById(item.product_id, item.product)}
                            compact
                            disabled={isFromOrder}
                            emptyLabel="—"
                            showCodeOnly
                          />
                        </LineItemField>
                      )}

                      <LineItemField label="Proy." className={DIC.project}>
                        <select
                          value={item.project_id || ''}
                          onChange={(e) => handleItemChange(index, 'project_id', e.target.value)}
                          className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                        >
                          <option value="">Seleccionar</option>
                          {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.code} - {project.name}
                            </option>
                          ))}
                        </select>
                      </LineItemField>

                      <LineItemField
                        label="Cant."
                        required
                        className={DIC.qty}
                        hint={
                          errors[`items.${index}.quantity`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.quantity`]}>
                              {errors[`items.${index}.quantity`]}
                            </span>
                          ) : (
                            <>
                          {isFromOrder && maxQuantity !== null && (
                                <span className="text-gray-500 whitespace-nowrap" title={`Máximo: ${maxQuantity}`}>
                                  máx.{maxQuantity}{' '}
                                </span>
                              )}
                              {item.item_type === 'product' && !item.uses_converted_display && (
                                <QuantityConversionHint
                                  product={product}
                                  unitCode={item.unit_of_measure}
                                  quantity={item.quantity}
                                />
                              )}
                            </>
                          )
                        }
                      >
                        <QuantityTextInput
                          value={item.quantity}
                          onChange={(qty) => handleItemChange(index, 'quantity', qty)}
                          decimalPlaces={4}
                          max={maxQuantity !== null ? maxQuantity : null}
                          min={isFromOrder ? 0.0001 : null}
                          blockNonPositive={isFromOrder}
                          disabled={!isQuantityEditable && isFromOrder}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                            errors[`items.${index}.quantity`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                          } ${!isQuantityEditable && isFromOrder ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          placeholder="0"
                        />
                      </LineItemField>

                      <LineItemField
                        label="P.Unit."
                        required
                        className={DIC.price}
                        hint={
                          errors[`items.${index}.unit_price`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.unit_price`]}>
                              {errors[`items.${index}.unit_price`]}
                            </span>
                          ) : (
                            <PriceIgvHint
                              netPrice={item.unit_price}
                              igvRate={formData.igv_rate ?? 18}
                              hasIgv={item.has_igv !== undefined ? item.has_igv : true}
                              pricesIncludeIgv={pricesIncludeIgv}
                            />
                          )
                        }
                      >
                        <div className="relative w-full">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                          <UnitPriceTextInput
                            netPrice={item.unit_price}
                            onNetPriceChange={(net) => handleItemChange(index, 'unit_price', net)}
                            pricesIncludeIgv={pricesIncludeIgv}
                            hasIgv={item.has_igv !== undefined ? item.has_igv : true}
                            igvRate={formData.igv_rate ?? 18}
                            className={`w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                              errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                            }`}
                            placeholder="0.0000"
                          />
                        </div>
                      </LineItemField>

                      <LineItemField label="IGV" className={DIC.igv}>
                        <select
                          value={item.has_igv !== undefined ? (item.has_igv ? 'true' : 'false') : 'true'}
                          onChange={(e) => handleItemChange(index, 'has_igv', e.target.value === 'true')}
                          className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      </LineItemField>

                      <LineItemField
                        label="Subtot."
                        className={DIC.subtotal}
                        hint={
                          (item.has_igv !== undefined ? item.has_igv : true) ? (
                            <span className="text-gray-500 truncate">
                              S/ {roundTo4(
                                subtotal + subtotal * ((formData.igv_rate ?? 18) / 100)
                              ).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} con IGV
                            </span>
                          ) : null
                        }
                      >
                        <div
                          className="w-full h-[34px] px-1.5 text-xs md:text-sm border border-gray-300 rounded-md bg-gray-50 text-right font-medium flex items-center justify-end truncate"
                          title={subtotal.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        >
                          S/ {subtotal.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                        </div>
                      </LineItemField>

                      {item.item_type === 'product' && (
                        <LineItemField label="Ingreso" required className={DIC.intake}>
                          <select
                            value={item.is_direct_consumption ? 'true' : 'false'}
                            onChange={(e) => handleItemChange(index, 'is_direct_consumption', e.target.value === 'true')}
                            className={`w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                              isFromEntryGuide ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            disabled={isFromEntryGuide}
                          >
                            <option value="false">Ingresar a Almacén</option>
                            <option value="true">Consumo Directo</option>
                          </select>
                        </LineItemField>
                      )}

                      <LineItemField hideLabel className={DIC.actions}>
                        <div className="flex flex-col items-center gap-1 w-full">
                        {((item.item_type === 'product' && product?.machinery_register_enabled && item.is_direct_consumption) || (item.item_type === 'service' && service?.machinery_register_enabled)) && (
                          <button
                            type="button"
                            onClick={() => setEventModalItemIndex(index)}
                              className="h-[34px] w-full flex items-center justify-center flex-shrink-0 bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200 transition-colors"
                            title="Registrar avance (evento de seguimiento)"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                            className="h-[34px] w-full flex items-center justify-center flex-shrink-0 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                          title="Eliminar item"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      </LineItemField>
                    </div>
                    {/* Nombre completo del producto o servicio y datos de vehículo/evento (debajo, sutil como al registrar) */}
                    {(item.product_id || item.service_id) && (() => {
                      const product = item.item_type === 'product' && item.product_id ? findProductById(item.product_id, item.product) : null;
                      const service = item.item_type === 'service' && item.service_id ? services.find(s => s.id === parseInt(item.service_id)) : null;
                      const name = product?.name ?? service?.name ?? null;
                      const pendingSummaries = pendingMachineryEvents.filter(e => e.itemIndex === index).map(e => e.displaySummary).filter(Boolean);
                      const loadedForItem = loadedMachineryEventsByItem.filter(e => e.itemIndex === index);
                      // Si se editó el evento en el modal, mostrar solo la línea actualizada (pending); si no, la cargada del documento
                      const summariesToShow = pendingSummaries.length > 0 ? pendingSummaries : loadedForItem.map((e) => e.displaySummary);
                      const hasEvents = summariesToShow.length > 0;
                      if (!name && !hasEvents) return null;
                      return (
                        <div className="mt-1.5 mb-0">
                          {name && (
                            <p className="text-xs text-gray-600 truncate" title={name}>
                              {name}
                            </p>
                          )}
                          {hasEvents && (
                            <div className="text-xs text-teal-600/90 mt-0.5 space-y-0.5">
                              {summariesToShow.map((summary, i) => (
                                <p key={i} className="truncate" title={summary}>{summary}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* Totales */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="w-full md:w-80 space-y-2">
                {(() => {
                  const calculated = calculateTotal();
                  const totals = amountOverride || calculated;
                  const base = Number(totals.baseImponible) || 0;
                  const noGrav = Number(totals.noGravados) || 0;
                  const igv = Number(totals.igv) || 0;
                  const total = Number(totals.total) || 0;
                  if (!isEditingAmounts) {
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Base Imponible:</span>
                          <span className="font-medium text-gray-700">
                            S/ {(base).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">IGV ({formData.igv_rate ?? 18}%):</span>
                          <span className="font-medium text-gray-700">
                            S/ {(igv).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">No Gravados:</span>
                          <span className="font-medium text-gray-700">
                            S/ {(noGrav).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-300">
                          <span className="text-base font-semibold text-gray-800">Total:</span>
                          <span className="text-2xl font-bold text-teal-600">
                            S/ {(total).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="pt-2">
                          {amountOverride ? (
                            <p className="text-xs text-amber-700 mb-1">IGV ajustado manualmente</p>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              setAmountForm({ igv_amount: String(igv) });
                              setIsEditingAmounts(true);
                            }}
                            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                            title="Ajustar IGV por diferencias de decimales"
                          >
                            Ajustar montos
                          </button>
                        </div>
                      </>
                    );
                  }
                  const igvNum = parseFloat(amountForm.igv_amount) || 0;
                  const calcForSave = calculateTotal();
                  const baseForSave =
                    calcForSave.baseImponible + calcForSave.noGravados > 0
                      ? calcForSave.baseImponible
                      : base;
                  const noGravForSave =
                    calcForSave.baseImponible + calcForSave.noGravados > 0
                      ? calcForSave.noGravados
                      : noGrav;
                  const totalRecalc = Math.round((baseForSave + igvNum + noGravForSave) * 100) / 100;
                  return (
                    <div className="pt-2 border-t border-gray-300 space-y-3">
                      <p className="text-xs text-gray-500">Modifique el IGV para corregir la diferencia por decimales. El Total se recalcula automáticamente.</p>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">IGV</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={amountForm.igv_amount}
                          onChange={(e) => setAmountForm({ igv_amount: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Total (recalculado)</label>
                        <p className="text-sm font-semibold text-teal-600 py-1.5">
                          S/ {totalRecalc.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setAmountOverride({
                              baseImponible: baseForSave,
                              igv: igvNum,
                              noGravados: noGravForSave,
                              total: totalRecalc,
                            });
                            setIsEditingAmounts(false);
                          }}
                          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-md"
                        >
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditingAmounts(false)}
                          className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-md"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Sección: Fechas de Servicio (solo si hay servicios) */}
        {formData.items.some(item => item.item_type === 'service') && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Fechas de Servicio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Inicio del Servicio
                </label>
                <input
                  type="date"
                  name="service_start_date"
                  value={formData.service_start_date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.service_start_date || errors.delivery_date
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                />
                {errors.service_start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.service_start_date}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Fin del Servicio
                </label>
                <input
                  type="date"
                  name="service_end_date"
                  value={formData.service_end_date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.service_start_date || errors.delivery_date
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                />
                {errors.service_start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.service_start_date}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sección: Fecha de Entrega (solo si hay productos) */}
        {formData.items.some(item => item.item_type === 'product') && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Fecha de Entrega
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Entrega de Productos
              </label>
              <input
                type="date"
                name="delivery_date"
                value={formData.delivery_date}
                onChange={handleChange}
                className={`w-full md:w-80 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.delivery_date || errors.service_start_date
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                }`}
              />
              {errors.delivery_date && (
                <p className="mt-1 text-sm text-red-600">{errors.delivery_date}</p>
              )}
            </div>
          </div>
        )}

        {/* Sección: Observaciones */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Observaciones
          </h3>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none"
            placeholder="Agregar observaciones o notas adicionales..."
          />
        </div>

        {/* Botones de Acción */}
        {!readOnly && (
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (document ? 'Actualizar Documento' : 'Crear Documento')}
            </button>
          </div>
        )}
      </div>
      </fieldset>
      {readOnly && onClose && (
        <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Volver
          </button>
        </div>
      )}
    </form>
  );

  // Modal: Selección de Órdenes
  // Obtener proveedores que tienen órdenes activas
  const getSuppliersWithOrders = () => {
    const supplierIds = new Set();
    
    // Agregar IDs de proveedores de órdenes de compra
    pendingPurchaseOrders.forEach(order => {
      if (order.supplier_id) {
        supplierIds.add(order.supplier_id);
      }
    });
    
    // Agregar IDs de proveedores de órdenes de servicio
    pendingServiceOrders.forEach(order => {
      if (order.supplier_id) {
        supplierIds.add(order.supplier_id);
      }
    });
    
    // Agregar IDs de proveedores de servicios continuos cerrados
    closedContinuousServices.forEach(service => {
      if (service.supplier_id) {
        supplierIds.add(service.supplier_id);
      }
    });
    
    // Agregar IDs de proveedores de guías de entrada
    pendingEntryGuides.forEach(guide => {
      if (guide.supplier_id) {
        supplierIds.add(guide.supplier_id);
      }
    });
    
    // Proveedores externos únicamente (excluir entidad interna "Producción")
    return suppliers.filter(
      (supplier) => supplierIds.has(supplier.id) && !supplier.is_internal,
    );
  };

  // Filtrar órdenes que tienen items con pending_quantity > 0
  const getOrdersWithPendingItems = (orders) => {
    return orders.filter(order => {
      if (!order.items || order.items.length === 0) return false;
      // Verificar si al menos un item tiene pending_quantity > 0
      return order.items.some(item => {
        const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity) : parseFloat(item.quantity);
        return pendingQty > 0;
      });
    });
  };

  const suppliersWithOrders = getSuppliersWithOrders();
  const filteredPurchaseOrders = selectedSupplierInModal 
    ? getOrdersWithPendingItems(pendingPurchaseOrders.filter(order => order.supplier_id === parseInt(selectedSupplierInModal)))
    : [];
  const filteredServiceOrders = selectedSupplierInModal 
    ? getOrdersWithPendingItems(pendingServiceOrders.filter(order => order.supplier_id === parseInt(selectedSupplierInModal)))
    : [];
  const filteredContinuousServices = selectedSupplierInModal 
    ? closedContinuousServices.filter(service => service.supplier_id === parseInt(selectedSupplierInModal))
    : [];
  const filteredEntryGuides = selectedSupplierInModal 
    ? pendingEntryGuides.filter(guide => guide.supplier_id === parseInt(selectedSupplierInModal))
    : [];

  const orderSelectorModal = (
    isOrderSelectorModalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Seleccionar referencias
            </h3>
            <button
              type="button"
              onClick={() => {
                setIsOrderSelectorModalOpen(false);
                setSelectedSupplierInModal('');
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">
            {referencesLoading ? (
              <div className="py-16 flex flex-col items-center justify-center text-gray-500">
                <FormLoadingSpinner />
                <p className="mt-4 text-sm">Cargando referencias pendientes…</p>
              </div>
            ) : (
            <>
            {/* Requerimientos internos (sin proveedor) */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Requerimientos internos</h4>
              <p className="text-xs text-gray-500 mb-3">
                Solicitudes de productos y cantidades (sin costos en el REQ). Marque y confirme con Aceptar.
              </p>
              <RequirementReferencePicker
                mode="multi"
                requirements={pendingRequirements}
                selectedIds={formData.selected_requirements}
                onToggle={handleSelectRequirement}
              />
            </div>

            <div className="border-t border-gray-200 pt-6 mb-6">
              <h4 className="text-md font-semibold text-gray-700 mb-3">Proveedores con órdenes activas y servicios continuos cerrados</h4>
              {suppliersWithOrders.length === 0 ? (
                <p className="text-sm text-gray-500">No hay proveedores con órdenes pendientes</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {suppliersWithOrders.map((supplier) => {
                    const purchaseOrdersCount = pendingPurchaseOrders.filter(po => po.supplier_id === supplier.id).length;
                    const serviceOrdersCount = pendingServiceOrders.filter(so => so.supplier_id === supplier.id).length;
                    const supplierContinuousServices = closedContinuousServices.filter(cs => cs.supplier_id === supplier.id);
                    const continuousServicesCount = supplierContinuousServices.length;
                    const continuousServicesRestante = supplierContinuousServices.reduce((sum, cs) => sum + (parseFloat(cs.pending_amount) || 0), 0);
                    const entryGuidesCount = pendingEntryGuides.filter(eg => eg.supplier_id === supplier.id).length;
                    const isSelected = selectedSupplierInModal === supplier.id.toString();
                    
                    return (
                      <button
                        key={supplier.id}
                        type="button"
                        onClick={() => setSelectedSupplierInModal(isSelected ? '' : supplier.id.toString())}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          isSelected 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium text-gray-900 mb-1">{supplier.name}</div>
                        <div className="text-xs text-gray-600">
                          {purchaseOrdersCount > 0 && (
                            <span>{purchaseOrdersCount} {purchaseOrdersCount === 1 ? 'orden de compra' : 'órdenes de compra'}</span>
                          )}
                          {purchaseOrdersCount > 0 && (serviceOrdersCount > 0 || continuousServicesCount > 0 || entryGuidesCount > 0) && <span> • </span>}
                          {serviceOrdersCount > 0 && (
                            <span>{serviceOrdersCount} {serviceOrdersCount === 1 ? 'orden de servicio' : 'órdenes de servicio'}</span>
                          )}
                          {(purchaseOrdersCount > 0 || serviceOrdersCount > 0) && continuousServicesCount > 0 && <span> • </span>}
                          {continuousServicesCount > 0 && (
                            <span>
                              {continuousServicesCount} {continuousServicesCount === 1 ? 'servicio continuo' : 'servicios continuos'}
                              {continuousServicesRestante > 0 && (
                                <> (S/ {continuousServicesRestante.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} restante)</>
                              )}
                            </span>
                          )}
                          {(purchaseOrdersCount > 0 || serviceOrdersCount > 0 || continuousServicesCount > 0) && entryGuidesCount > 0 && <span> • </span>}
                          {entryGuidesCount > 0 && (
                            <span>{entryGuidesCount} {entryGuidesCount === 1 ? 'guía de entrada' : 'guías de entrada'}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Órdenes de Compra Pendientes */}
            {selectedSupplierInModal && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-3">Órdenes de Compra Pendientes</h4>
                {filteredPurchaseOrders.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay órdenes de compra pendientes para este proveedor</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredPurchaseOrders.map((order) => {
                    const isSelected = formData.selected_purchase_orders.includes(order.id);
                    // Calcular progreso basado en pending_quantity
                    // Progreso = (cantidad usada / cantidad total) * 100
                    // cantidad usada = quantity - pending_quantity
                    const orderProgress = order.items?.length > 0
                      ? Math.round((order.items.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity);
                          const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity) : qty;
                          const usedQty = qty - pendingQty;
                          return sum + usedQty;
                        }, 0) / 
                        order.items.reduce((sum, item) => sum + parseFloat(item.quantity), 0)) * 100)
                      : 0;
                    
                    return (
                      <label
                        key={order.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-teal-50 border-teal-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectPurchaseOrder(order.id, e.target.checked)}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{order.code}</span>
                            <span className="text-sm text-gray-600">
                              S/ {parseFloat(order.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {order.supplier?.name} • {new Date(order.order_date).toLocaleDateString('es-PE')}
                          </div>
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className="bg-teal-600 h-1.5 rounded-full" 
                                  style={{ width: `${orderProgress}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">{orderProgress}%</span>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Órdenes de Servicio Pendientes */}
            {selectedSupplierInModal && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-3">Órdenes de Servicio Pendientes</h4>
                {filteredServiceOrders.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay órdenes de servicio pendientes para este proveedor</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredServiceOrders.map((order) => {
                    const isSelected = formData.selected_service_orders.includes(order.id);
                    // Calcular progreso basado en pending_quantity
                    const orderProgress = order.items?.length > 0
                      ? Math.round((order.items.reduce((sum, item) => {
                          const qty = parseFloat(item.quantity);
                          const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity) : qty;
                          const usedQty = qty - pendingQty;
                          return sum + usedQty;
                        }, 0) / 
                        order.items.reduce((sum, item) => sum + parseFloat(item.quantity), 0)) * 100)
                      : 0;
                    
                    return (
                      <label
                        key={order.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-teal-50 border-teal-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectServiceOrder(order.id, e.target.checked)}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{order.code}</span>
                            <span className="text-sm text-gray-600">
                              S/ {parseFloat(order.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {order.supplier?.name} • {new Date(order.order_date).toLocaleDateString('es-PE')}
                          </div>
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className="bg-teal-600 h-1.5 rounded-full" 
                                  style={{ width: `${orderProgress}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">{orderProgress}%</span>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              </div>
            )}

            {/* Servicios Continuos Cerrados */}
            {selectedSupplierInModal && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-3">Servicios Continuos Cerrados</h4>
                {filteredContinuousServices.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay servicios continuos cerrados para este proveedor</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredContinuousServices.map((service) => {
                    const isSelected = (formData.selected_continuous_services || []).includes(service.id);
                    const progressCount = service.progress?.length || 0;
                    
                    return (
                      <label
                        key={service.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-teal-50 border-teal-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectContinuousService(service.id, e.target.checked)}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{service.code}</span>
                            <span className="text-sm text-gray-600">
                              S/ {parseFloat(service.pending_amount ?? service.total_amount ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} restante
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {service.supplier?.name} • {service.start_date ? formatDateLocaleLima(service.start_date) : 'N/A'}
                            {service.end_date && ` - ${formatDateLocaleLima(service.end_date)}`}
                          </div>
                          {progressCount > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {progressCount} {progressCount === 1 ? 'registro de avance' : 'registros de avance'}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              </div>
            )}

            {/* Guías de Entrada Pendientes */}
            {selectedSupplierInModal && filteredEntryGuides.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-700 mb-3">Guías de Entrada Pendientes</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {filteredEntryGuides.map((guide) => {
                    const isSelected = formData.selected_entry_guides?.includes(guide.id) || false;
                    
                    return (
                      <label
                        key={guide.id}
                        className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectEntryGuide(guide.id, e.target.checked)}
                          className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900">{guide.code}</span>
                            <span className="text-xs text-gray-500">
                              {guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A'}
                            </span>
                          </div>
                          {guide.warehouse && (
                            <div className="text-xs text-gray-600">
                              Almacén: {guide.warehouse.code} {guide.warehouse.name ? `- ${guide.warehouse.name}` : ''}
                            </div>
                          )}
                          {guide.total_amount && (
                            <div className="text-xs text-gray-600 mt-1">
                              Total: {guide.currency || 'PEN'} {parseFloat(guide.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mensaje cuando no hay proveedor seleccionado */}
            {!selectedSupplierInModal && suppliersWithOrders.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Selecciona un proveedor para ver sus órdenes y guías pendientes
                </p>
              </div>
            )}

            {/* Botones del Modal */}
            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsOrderSelectorModalOpen(false)}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={applySelectedRequirementsFromModal}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                Aceptar
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    )
  );

  if (isPageMode) {
    return (
      <>
        {isInitialLoading ? (
          <FormLoadingSpinner
            hint="Preparando proveedores, servicios y datos del documento"
          />
        ) : formContent}
        {orderSelectorModal}
        <ComprasItemsImportPreviewModal
          isOpen={!!importPreview}
          onClose={() => setImportPreview(null)}
          onConfirm={handleConfirmImportPreview}
          fileName={importPreview?.fileName}
          items={importPreview?.items || []}
          errors={importPreview?.errors || []}
          warnings={importPreview?.warnings || []}
          projects={projects}
          products={products}
          units={units}
          showTipo
          confirmLabel="Añadir al documento"
          entityLabel="el documento"
        />
        <CreateSupplierModal
          isOpen={isCreateSupplierModalOpen}
          onClose={() => setIsCreateSupplierModalOpen(false)}
          onSuccess={handleSupplierCreated}
          forceProviderOnly={true}
        />
        {createConfirmModal}
        {editConfirmModal}
        {activosEnabled && (
          <DocumentActivoModal
            isOpen={isActivoModalOpen}
            onClose={() => {
              setIsActivoModalOpen(false);
              setEditingActivoIndex(null);
            }}
            onSave={handleSaveDocumentActivo}
            documentContext={documentActivoContext}
            initialActivo={editingActivoIndex != null ? pendingActivos[editingActivoIndex] : null}
            suggestedCode={suggestedActivoCode}
          />
        )}
        <MachineryEventModal
          isOpen={eventModalItemIndex !== null}
          onClose={() => setEventModalItemIndex(null)}
          onSuccess={(eventData) => {
            if (eventData && eventModalItemIndex != null) {
              setPendingMachineryEvents(prev => prev.filter(e => e.itemIndex !== eventModalItemIndex).concat([{ itemIndex: eventModalItemIndex, ...eventData }]));
              setLastEventFormByItem(prev => ({ ...prev, [eventModalItemIndex]: eventData }));
              setToast({ isOpen: true, message: 'Evento registrado. Se enviará con el documento.', type: 'success' });
            }
            setEventModalItemIndex(null);
          }}
          productName={eventModalItemIndex != null ? (formData.items[eventModalItemIndex]?.product_id
            ? (findProductById(formData.items[eventModalItemIndex].product_id)?.name ?? '')
            : formData.items[eventModalItemIndex]?.service_id
              ? (services.find(s => s.id === parseInt(formData.items[eventModalItemIndex].service_id))?.name ?? '')
              : '') : ''}
        sourceEventDate={formData.delivery_date || formData.issue_date || null}
        collectOnly={true}
        sourceProjectId={eventModalItemIndex != null && formData.items[eventModalItemIndex]?.project_id ? String(formData.items[eventModalItemIndex].project_id) : null}
        initialFormData={eventModalItemIndex != null ? (() => {
            const saved = lastEventFormByItem[eventModalItemIndex];
            const item = formData.items[eventModalItemIndex];
            const itemProjectId = item?.project_id != null && item.project_id !== '' ? String(item.project_id) : '';
            const savedProjectId = (saved?.project_id != null && saved.project_id !== '') ? saved.project_id : '';
            return { ...(saved || {}), project_id: itemProjectId || savedProjectId };
          })() : null}
        />
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
         <SunatSearchResultModal
          isOpen={sunatModalOpen}
          onClose={() => setSunatModalOpen(false)}
          onApply={handleSunatApply}
          data={sunatData}
          products={products}
          getProduct={findProductById}
          productSelectorAsyncProps={productSelectorAsyncProps}
          onEnsureProducts={ensureByIds}
          units={units}
          supplierId={formData.supplier_id ? parseInt(formData.supplier_id) : null}
        />
      </>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-800">
            {document ? 'Editar Documento' : 'Nuevo Documento'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {isInitialLoading ? (
            <FormLoadingSpinner
              compact
              hint="Preparando proveedores, servicios y datos del documento"
            />
          ) : formContent}
        </div>
      </div>

      {/* Modal de selección de órdenes */}
      {orderSelectorModal}

      {createConfirmModal}
      {editConfirmModal}

      <ComprasItemsImportPreviewModal
        isOpen={!!importPreview}
        onClose={() => setImportPreview(null)}
        onConfirm={handleConfirmImportPreview}
        fileName={importPreview?.fileName}
        items={importPreview?.items || []}
        errors={importPreview?.errors || []}
        warnings={importPreview?.warnings || []}
        projects={projects}
        products={products}
        units={units}
        showTipo
        confirmLabel="Añadir al documento"
        entityLabel="el documento"
      />

      {/* Modal de crear proveedor */}
      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        forceProviderOnly={true}
      />

      {/* Modal de registro de evento de seguimiento (bitácora) cuando el producto tiene "registrar avance" */}
      <MachineryEventModal
        isOpen={eventModalItemIndex !== null}
        onClose={() => setEventModalItemIndex(null)}
        onSuccess={(eventData) => {
          if (eventData && eventModalItemIndex != null) {
            setPendingMachineryEvents(prev => prev.filter(e => e.itemIndex !== eventModalItemIndex).concat([{ itemIndex: eventModalItemIndex, ...eventData }]));
            setLastEventFormByItem(prev => ({ ...prev, [eventModalItemIndex]: eventData }));
            setToast({ isOpen: true, message: 'Evento registrado. Se enviará con el documento.', type: 'success' });
          }
          setEventModalItemIndex(null);
        }}
        productName={eventModalItemIndex != null ? (formData.items[eventModalItemIndex]?.product_id
          ? (findProductById(formData.items[eventModalItemIndex].product_id)?.name ?? '')
          : formData.items[eventModalItemIndex]?.service_id
            ? (services.find(s => s.id === parseInt(formData.items[eventModalItemIndex].service_id))?.name ?? '')
            : '') : ''}
        sourceEventDate={formData.delivery_date || formData.issue_date || null}
        collectOnly={true}
        sourceProjectId={eventModalItemIndex != null && formData.items[eventModalItemIndex]?.project_id ? String(formData.items[eventModalItemIndex].project_id) : null}
        initialFormData={eventModalItemIndex != null ? (() => {
          const saved = lastEventFormByItem[eventModalItemIndex];
          const item = formData.items[eventModalItemIndex];
          const itemProjectId = item?.project_id != null && item.project_id !== '' ? String(item.project_id) : '';
          const savedProjectId = (saved?.project_id != null && saved.project_id !== '') ? saved.project_id : '';
          return { ...(saved || {}), project_id: itemProjectId || savedProjectId };
        })() : null}
      />

      {activosEnabled && (
        <DocumentActivoModal
          isOpen={isActivoModalOpen}
          onClose={() => {
            setIsActivoModalOpen(false);
            setEditingActivoIndex(null);
          }}
          onSave={handleSaveDocumentActivo}
          documentContext={documentActivoContext}
          initialActivo={editingActivoIndex != null ? pendingActivos[editingActivoIndex] : null}
          suggestedCode={suggestedActivoCode}
        />
      )}

      <ConfirmModal
        isOpen={closePoConfirm.open}
        title="Cerrar orden de compra"
        message={`¿Marcar la orden ${closePoConfirm.code} como completada? Ya no aparecerá como pendiente aunque queden cantidades sin documentar en la OC.`}
        confirmText={closingPurchaseOrder ? 'Cerrando…' : 'Sí, cerrar orden'}
        cancelText="Cancelar"
        onConfirm={handleClosePurchaseOrderFulfillment}
        onClose={() => setClosePoConfirm({ open: false, orderId: null, code: '' })}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      <SunatSearchResultModal
        isOpen={sunatModalOpen}
        onClose={() => setSunatModalOpen(false)}
        onApply={handleSunatApply}
        data={sunatData}
        products={products}
        getProduct={findProductById}
        productSelectorAsyncProps={productSelectorAsyncProps}
        onEnsureProducts={ensureByIds}
        units={units}
        supplierId={formData.supplier_id ? parseInt(formData.supplier_id) : null}
      />
    </div>
  );
};

export default DocumentForm;

